import React, { FC, useEffect, useRef, useState } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Statistic,
  Progress,
  Timeline,
  Button,
  Empty,
  Dropdown,
  message,
  Popover,
  Modal,
  Input,
  Drawer,
  DatePicker,
  Table,
  Tooltip,
} from "antd";
import {
  computeTaskPriceBreakdown,
  DEFAULT_CESS_PERCENT,
  DEFAULT_GST_PERCENT,
} from "../shared/taskPrice";
import { rateInclGstTooltip } from "../shared/TaskPriceBreakdownPanel";
import {
  UserOutlined,
  CalendarOutlined,
  TagsOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  PercentageOutlined,
  EditOutlined,
  DollarOutlined,
  TeamOutlined,
  ToolOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  DownOutlined,
  NumberOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  BellOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  RightOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import Swal, { SweetAlertIcon } from "sweetalert2";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { getColorByLabel } from "../projects/ProjectInformationById";
import { formatRecurrence } from "./TaskInformationById";
import { tasksApis } from "../../../api/NewTasks";
import { workApprovalApis } from "../../../api/WorkApproval";
import { updateTaskListDetailsModalSlice } from "../../../store/slices/projectManagement/task-list-view/TaskListDetailModalSlice";
import TaskCheckListCreateModal from "./TaskCheckListCreateModal";
import ApplyNormModal from "../norms/ApplyNormModal";
import ApplyManpowerNormModal from "../norms/ApplyManpowerNormModal";
import ApplyMachineryNormModal from "../norms/ApplyMachineryNormModal";
import ApplyDeploymentNormModal from "../norms/ApplyDeploymentNormModal";
import { EditAccess } from "../../../components/accessControl/AccessControl";
import { normsApis } from "../../../api/Norms";
import { manpowerApis } from "../../../api/ManpowerNorms";
import { machineryApis } from "../../../api/MachineryNorms";
import { deploymentNormApis } from "../../../api/MachineryEquipment";
import {
  MACHINERY_HEADS,
  machineryHeadLabel,
} from "../machinery-norms/machineryHeads";
import { headTagColor } from "../reports/MachineryCostAnalysis/machineryShared";
import SectionCard from "../shared/SectionCard";
import RateUnitToggle from "../shared/RateUnitToggle";
import { moneyPerUnit, useRateUnit } from "../shared/useRateUnit";
import TaskScheduleSection from "./TaskScheduleSection";
import TaskActualSection from "./TaskActualSection";
import {
  statusTone,
  GenericStatusPill,
  PriorityPill,
} from "../shared/statusColors";
// This card also renders outside ProjectPageShell (e.g. modal flows), so pull
// in the shared chrome styles directly.
import "../shared/project.css";
import "../projects/hub/hub.css";

interface TaskQuickViewCardProps {
  onEdit?: () => void;
  showEditButton?: boolean;
  onRefresh?: () => void;
}

const formatDate = (d: any) =>
  d ? dayjs(d).format("DD MMM YYYY") : "Not Set";

// Build a compact "Label: value · Label: value" meta line, skipping empty parts.
const buildMeta = (parts: Array<[string, any]>) =>
  parts
    .filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && `${v}` !== "0",
    )
    .map(([label, v]) => `${label}: ${v}`)
    .join("  ·  ");

const lineMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#6b7280",
  marginTop: 3,
};

// Currency formatter (INR, 2 decimals) and a section-total summer.
const fmtAmount = (n: number) =>
  `₹${(Number.isFinite(n) ? n : 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const sumAmount = (arr: any[], key = "totalAmount") =>
  (arr || []).reduce((sum, item) => sum + (Number(item?.[key]) || 0), 0);

// Footer shown at the bottom of each allocation card.
const sectionTotalStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 6,
  marginTop: 8,
  paddingTop: 6,
  borderTop: "1px dashed #d9d9d9",
  fontSize: 12,
  fontWeight: 600,
  color: "#004293",
};

// Rate-analysis (unit-basis) line total.
const raLineTotal = (x: any) =>
  (Number(x?.rateAnalysisQty) || 0) * (Number(x?.rateAnalysisRate) || 0);

// Renders a line's "Rate Analysis (per unit)" and "Overall" breakdown as two
// clearly-separated colour-coded rows (matching the task creation screen).
const GroupRows: FC<{
  line: any;
  overallQty: any;
  overallUom?: string;
  showRa?: boolean;
  raLabel?: string;
  overallLabel?: string;
}> = ({
  line,
  overallQty,
  overallUom,
  showRa = true,
  raLabel = "Rate Analysis",
  overallLabel = "Overall",
}) => {
  const hasRA =
    showRa && line?.rateAnalysisQty != null && line?.rateAnalysisQty !== "";
  // No rate-analysis for this task → show a single plain "overall" line.
  if (!showRa) {
    return (
      <div style={lineMetaStyle}>
        {overallQty != null && overallQty !== ""
          ? `Qty: ${overallQty}${overallUom ? ` ${overallUom}` : ""}  ·  `
          : ""}
        Rate: {fmtAmount(Number(line.rate) || 0)} · Total:{" "}
        {fmtAmount(Number(line.totalAmount) || 0)}
      </div>
    );
  }
  // Colour-coded rows (no repeated text label — a single legend explains the
  // blue = Rate Analysis / green = Overall colours).
  return (
    <>
      {hasRA && (
        <div
          title={raLabel}
          style={{
            background: "#f0f7ff",
            borderLeft: "3px solid #1677ff",
            borderRadius: 4,
            padding: "2px 6px",
            marginTop: 3,
            fontSize: 11,
            color: "#374151",
          }}
        >
          {line.rateAnalysisQty}
          {line.rateAnalysisUomName ? ` ${line.rateAnalysisUomName}` : ""} ·{" "}
          {fmtAmount(Number(line.rateAnalysisRate) || 0)} ·{" "}
          <strong>{fmtAmount(raLineTotal(line))}</strong>
        </div>
      )}
      <div
        title={overallLabel}
        style={{
          background: "#f6ffed",
          borderLeft: "3px solid #52c41a",
          borderRadius: 4,
          padding: "2px 6px",
          marginTop: 3,
          fontSize: 11,
          color: "#374151",
        }}
      >
        {overallQty != null && overallQty !== ""
          ? `${overallQty}${overallUom ? ` ${overallUom}` : ""}`
          : "—"}{" "}
        · {fmtAmount(Number(line.rate) || 0)} ·{" "}
        <strong>{fmtAmount(Number(line.totalAmount) || 0)}</strong>
      </div>
    </>
  );
};

// Column-wise allocation list: a header row ("Item | Rate Analysis | Overall")
// shown once, with each item's values lined up under those columns. Avoids
// repeating the labels on every row.
interface AllocGroup {
  qty: any;
  uom?: string;
  rate: any;
  total: number;
}
const AllocList: FC<{
  items: any[];
  hasRa: boolean;
  raLabel: string;
  overallLabel: string;
  itemHeader?: string;
  getName: (x: any) => React.ReactNode;
  getRa: (x: any) => AllocGroup | null;
  getOverall: (x: any) => AllocGroup;
  renderExtra?: (x: any) => React.ReactNode;
  emptyText?: string;
}> = ({
  items,
  hasRa,
  raLabel,
  overallLabel,
  itemHeader = "Item",
  getName,
  getRa,
  getOverall,
  renderExtra,
  emptyText = "None",
}) => {
  if (!items || !items.length)
    return <span style={{ color: "#999", fontSize: 12 }}>{emptyText}</span>;
  const grid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: hasRa ? "1.5fr 1.2fr 1.2fr" : "1.7fr 1.3fr",
    gap: 8,
    alignItems: "center",
  };
  const cell = (g: AllocGroup | null) =>
    g ? (
      <span>
        {g.qty != null && g.qty !== ""
          ? `${g.qty}${g.uom ? ` ${g.uom}` : ""} · `
          : ""}
        {fmtAmount(Number(g.rate) || 0)} ·{" "}
        <strong>{fmtAmount(g.total)}</strong>
      </span>
    ) : (
      <span style={{ color: "#bbb" }}>—</span>
    );
  return (
    <div>
      {/* Header */}
      <div
        style={{
          ...grid,
          fontSize: 11,
          fontWeight: 600,
          color: "#6b7280",
          padding: "2px 4px 4px",
          borderBottom: "1px solid #eef0f2",
        }}
      >
        <span>{itemHeader}</span>
        {hasRa && <span style={{ color: "#1677ff" }}>{raLabel}</span>}
        <span style={{ color: "#237804" }}>{overallLabel}</span>
      </div>
      {items.map((x: any, i: number) => (
        <div
          key={i}
          style={{
            padding: "7px 6px",
            borderRadius: 6,
            marginTop: i === 0 ? 6 : 4,
            background: i % 2 === 0 ? "#ffffff" : "#fbfcfd",
            border: "1px solid #eef0f2",
          }}
        >
          <div style={grid}>
            <div style={{ minWidth: 0, fontSize: 12 }}>{getName(x)}</div>
            {hasRa && (
              <div
                style={{
                  fontSize: 11,
                  background: "#f0f7ff",
                  borderRadius: 4,
                  padding: "2px 6px",
                  color: "#374151",
                }}
              >
                {cell(getRa(x))}
              </div>
            )}
            <div
              style={{
                fontSize: 11,
                background: "#f6ffed",
                borderRadius: 4,
                padding: "2px 6px",
                color: "#374151",
              }}
            >
              {cell(getOverall(x))}
            </div>
          </div>
          {renderExtra && renderExtra(x)}
        </div>
      ))}
    </div>
  );
};

// Read-only list of a tool's split-materials (diesel, spares, ...).
const ToolMaterialsView: FC<{
  materials: any[];
  materialMap: Record<number, string>;
  subMaterialMap: Record<number, string>;
  showRa?: boolean;
  raLabel?: string;
  overallLabel?: string;
  showHeader?: boolean;
}> = ({
  materials,
  materialMap,
  subMaterialMap,
  showRa = true,
  raLabel,
  overallLabel,
  showHeader = true,
}) => {
  if (!materials || !materials.length) return null;
  return (
    <div>
      {showHeader && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#8c8c8c",
            marginBottom: 4,
          }}
        >
          Split Materials ({materials.length})
        </div>
      )}
      <AllocList
        items={materials}
        hasRa={showRa}
        raLabel={raLabel || "Rate Analysis"}
        overallLabel={overallLabel || "Overall"}
        itemHeader="Material / Sub-material"
        getName={(m: any) => (
          <span style={{ color: "#374151" }}>
            {materialMap[m.materialId] || `Material #${m.materialId}`}
            {m.subMaterialId
              ? ` / ${subMaterialMap[m.subMaterialId] || `#${m.subMaterialId}`}`
              : ""}
          </span>
        )}
        getRa={(m: any) =>
          m.rateAnalysisQty != null
            ? {
                qty: m.rateAnalysisQty,
                uom: m.rateAnalysisUomName,
                rate: m.rateAnalysisRate,
                total: raLineTotal(m),
              }
            : null
        }
        getOverall={(m: any) => ({
          qty: m.quantity,
          uom: m.workUomName,
          rate: m.rate,
          total: Number(m.totalAmount) || 0,
        })}
      />
    </div>
  );
};

// Eyebrow label used on the 3M summary cards (matches Project3MSummary).
const summaryEyebrow: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#8094ae",
};

// One compact 3M summary card: icon chip + eyebrow + big ink total, a
// per-unit / line-count meta line, an optional applied-norm row, and a footer
// with apply/re-apply actions on the left and a "View list →" on the right.
const SummaryMCard: FC<{
  icon: React.ReactNode;
  color: string;
  tint: string;
  label: string;
  total: number;
  perUnit: React.ReactNode;
  lineCount: number;
  normRow?: React.ReactNode;
  actions?: React.ReactNode;
  onViewList: () => void;
}> = ({
  icon,
  color,
  tint,
  label,
  total,
  perUnit,
  lineCount,
  normRow,
  actions,
  onViewList,
}) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e4e9f0",
      borderRadius: 12,
      padding: "14px 16px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      boxShadow:
        "0 1px 3px rgba(2, 44, 87, 0.06), 0 3px 10px rgba(2, 44, 87, 0.05)",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: tint,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 17,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={summaryEyebrow}>{label}</div>
        <div
          style={{
            fontFamily: "Inter, Nunito, sans-serif",
            fontSize: 20,
            fontWeight: 700,
            color: "#022c57",
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.25,
          }}
        >
          {fmtAmount(total)}
        </div>
      </div>
    </div>
    <div style={{ fontSize: 12, color: "#8094ae", marginTop: 8 }}>
      {perUnit}
      <span style={{ margin: "0 6px", color: "#d6dce6" }}>·</span>
      {lineCount} line{lineCount === 1 ? "" : "s"}
    </div>
    {normRow != null && (
      <div
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: "1px solid #eef1f6",
        }}
      >
        {normRow}
      </div>
    )}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        marginTop: "auto",
        paddingTop: 12,
      }}
    >
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>
      <Button
        type="link"
        size="small"
        style={{ padding: 0, flexShrink: 0, fontSize: 13 }}
        onClick={onViewList}
      >
        View list <RightOutlined style={{ fontSize: 10 }} />
      </Button>
    </div>
  </div>
);

const TaskQuickViewCard: FC<TaskQuickViewCardProps> = ({
  onEdit,
  showEditButton = true,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const [messageApi, msgContext] = message.useMessage();
  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal
  );
  const { data: currentUser } = useSelector(
    (state: RootState) => (state as any).currentUser
  );
  const [statusList, setStatusList] = useState<
    { label: string; key: string; value: string }[]
  >([]);
  const [statusUpdating, setStatusUpdating] = useState(false);
  // Shared app-wide preference — flipping it here also flips the fleet list.
  const [machineryRateUnit] = useRateUnit();
  const [applyNormOpen, setApplyNormOpen] = useState(false);
  const [normGroups, setNormGroups] = useState<any[]>([]);
  const [normMatLoading, setNormMatLoading] = useState(false);
  const [removingNormId, setRemovingNormId] = useState<number | null>(null);
  // Manpower-from-norm (PRW): the applied labour requirement(s) on this task,
  // a flat list (manpower is a flat rate card). Decoupled from BOQ/budget.
  const [applyManpowerOpen, setApplyManpowerOpen] = useState(false);
  const [manpowerItems, setManpowerItems] = useState<any[]>([]);
  const [manpowerLoading, setManpowerLoading] = useState(false);
  const [removingManpowerId, setRemovingManpowerId] = useState<number | null>(null);
  // Machinery-from-norm: the applied machinery requirement(s) on this task, a
  // flat list (activity-scoped machinery rate card). Decoupled from tools.
  const [applyMachineryOpen, setApplyMachineryOpen] = useState(false);
  const [machineryNormItems, setMachineryNormItems] = useState<any[]>([]);
  const [machineryNormLoading, setMachineryNormLoading] = useState(false);
  const [removingMachineryId, setRemovingMachineryId] = useState<number | null>(
    null,
  );
  // Deployment-norm applications: a PARALLEL block to the machinery norms
  // above — versioned per-BoQ-code equipment matrices applied to this task.
  // Only the quantity is frozen; lines/hours/amounts arrive LIVE-derived from
  // the version-in-force + equipment rates (strings on the wire). Its amounts
  // now JOIN machineryTotal (user decision, Aug 10) — note the total moves when
  // rates or matrices are re-versioned, since these amounts are live-derived.
  const [applyDeploymentOpen, setApplyDeploymentOpen] = useState(false);
  const [deploymentData, setDeploymentData] = useState<any | null>(null);
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [removingDeploymentId, setRemovingDeploymentId] = useState<
    number | null
  >(null);
  // 3M drill-down drawer: which resource's line list is open.
  const [drawerKind, setDrawerKind] = useState<
    "material" | "manpower" | "machinery" | null
  >(null);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  // Re-apply ("swap on apply") flags — drive the shared apply modals in replace mode.
  const [reapplyNorm, setReapplyNorm] = useState(false);
  const [reapplyManpower, setReapplyManpower] = useState(false);
  const [reapplyMachinery, setReapplyMachinery] = useState(false);
  const [reapplyDeployment, setReapplyDeployment] = useState(false);
  // Which head the apply modal was opened for (per-head Re-apply / Apply).
  const [reapplyMachineryHead, setReapplyMachineryHead] = useState<
    string | null
  >(null);

  // Executed Rate modal state (UnderQC → Closed and any → Closed)
  const [executedRateModalVisible, setExecutedRateModalVisible] =
    useState(false);
  const [executedRate, setExecutedRate] = useState<number | null>(null);
  const [rateRemarks, setRateRemarks] = useState("");
  const [pendingClosureStatus, setPendingClosureStatus] = useState<
    string | null
  >(null);

  // OnHold modal state (InProgress/UnderQC/Active/Open → OnHold). Captures the
  // hold reason + expected resume date inline instead of routing to the edit form.
  const [onHoldModalVisible, setOnHoldModalVisible] = useState(false);
  const [onHoldReason, setOnHoldReason] = useState("");
  const [onHoldETA, setOnHoldETA] = useState<dayjs.Dayjs | null>(null);
  const [pendingOnHoldStatus, setPendingOnHoldStatus] = useState<string | null>(
    null
  );

  // Checklist verification modal state (sequential checklist prompts)
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [selectedChecklistForModal, setSelectedChecklistForModal] = useState<{
    checklistId: number | null;
    checklistLinkId: number | null;
  } | null>(null);
  const pendingStatusRef = useRef<string | null>(null);

  const task: any = TaskListInDetailModal?.selectedTaskDetails;

  // Materials-from-norm: the norm requirement(s) applied to this task, grouped
  // by norm. Decoupled from the BOQ — read from norm_task_applications.
  const fetchNormMaterials = React.useCallback(async () => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;
    try {
      setNormMatLoading(true);
      const res = await normsApis.getTaskNormMaterials(taskId);
      if (res?.status === true) {
        setNormGroups(Array.isArray(res?.data?.norms) ? res.data.norms : []);
      }
    } catch {
      /* non-fatal — leave the list as-is */
    } finally {
      setNormMatLoading(false);
    }
  }, [task?.taskId]);

  useEffect(() => {
    fetchNormMaterials();
  }, [fetchNormMaterials]);

  const handleRemoveNorm = async (normId: number, normLabel: string) => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;

    const { isConfirmed, value: reason } = await Swal.fire({
      title: "Remove norm from task?",
      icon: "warning",
      html: `Removing <b>${normLabel}</b> deletes all material requirement lines it contributed to this task. This can't be undone — you'd need to re-apply the norm to restore them.`,
      input: "textarea",
      inputLabel: "Reason for removal",
      inputPlaceholder: "Why is this norm being removed?",
      inputAttributes: { "aria-label": "Reason for removal" },
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      inputValidator: (v: string) =>
        !v || !v.trim() ? "Please enter a reason" : null,
    });
    if (!isConfirmed) return;

    try {
      setRemovingNormId(normId);
      const res = await normsApis.removeNormFromTask({
        taskId,
        normId,
        reason: (reason || "").trim(),
      });
      if (res?.status === true) {
        await fetchNormMaterials();
      } else {
        messageApi.error(
          res?.data?.message || res?.message || "Failed to remove the norm",
        );
      }
    } catch (err: any) {
      messageApi.error(err?.message || "Failed to remove the norm");
    } finally {
      setRemovingNormId(null);
    }
  };

  // Manpower-from-norm (PRW): the labour requirement(s) applied to this task,
  // a flat list read from manpower_norm_task_applications.
  const fetchManpowerItems = React.useCallback(async () => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;
    try {
      setManpowerLoading(true);
      const res = await manpowerApis.getTaskManpowerNorms(taskId);
      if (res?.status === true) {
        setManpowerItems(Array.isArray(res?.data?.items) ? res.data.items : []);
      }
    } catch {
      /* non-fatal — leave the list as-is */
    } finally {
      setManpowerLoading(false);
    }
  }, [task?.taskId]);

  useEffect(() => {
    fetchManpowerItems();
  }, [fetchManpowerItems]);

  // Remove is per-item (each chip), unlike the material per-norm remove.
  const handleRemoveManpower = async (
    manpowerNormId: number,
    itemLabel: string,
  ) => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;

    const { isConfirmed, value: reason } = await Swal.fire({
      title: "Remove manpower item from task?",
      icon: "warning",
      html: `Removing <b>${itemLabel}</b> deletes its labour requirement line on this task. This can't be undone — you'd need to re-apply it to restore.`,
      input: "textarea",
      inputLabel: "Reason for removal",
      inputPlaceholder: "Why is this manpower item being removed?",
      inputAttributes: { "aria-label": "Reason for removal" },
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      inputValidator: (v: string) =>
        !v || !v.trim() ? "Please enter a reason" : null,
    });
    if (!isConfirmed) return;

    try {
      setRemovingManpowerId(manpowerNormId);
      const res = await manpowerApis.removeFromTask({
        taskId,
        manpowerNormId,
        reason: (reason || "").trim(),
      });
      if (res?.status === true) {
        await fetchManpowerItems();
      } else {
        messageApi.error(
          res?.data?.message || res?.message || "Failed to remove the manpower item",
        );
      }
    } catch (err: any) {
      messageApi.error(err?.message || "Failed to remove the manpower item");
    } finally {
      setRemovingManpowerId(null);
    }
  };

  // Machinery-from-norm: the machinery requirement(s) applied to this task, a
  // flat list read from the machinery norm task applications.
  const fetchMachineryNormItems = React.useCallback(async () => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;
    try {
      setMachineryNormLoading(true);
      const res = await machineryApis.getTaskMachineryNorms(taskId);
      if (res?.status === true) {
        setMachineryNormItems(
          Array.isArray(res?.data?.items) ? res.data.items : [],
        );
      }
    } catch {
      /* non-fatal — leave the list as-is */
    } finally {
      setMachineryNormLoading(false);
    }
  }, [task?.taskId]);

  useEffect(() => {
    fetchMachineryNormItems();
  }, [fetchMachineryNormItems]);

  // Remove is per-norm (each chip), same pattern as the manpower per-item remove.
  const handleRemoveMachinery = async (
    machineryNormId: number,
    normLabel: string,
  ) => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;

    const { isConfirmed, value: reason } = await Swal.fire({
      title: "Remove machinery norm from task?",
      icon: "warning",
      html: `Removing <b>${normLabel}</b> deletes its machinery requirement line on this task. This can't be undone — you'd need to re-apply it to restore.`,
      input: "textarea",
      inputLabel: "Reason for removal",
      inputPlaceholder: "Why is this machinery norm being removed?",
      inputAttributes: { "aria-label": "Reason for removal" },
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      inputValidator: (v: string) =>
        !v || !v.trim() ? "Please enter a reason" : null,
    });
    if (!isConfirmed) return;

    try {
      setRemovingMachineryId(machineryNormId);
      const res = await machineryApis.removeFromTask({
        taskId,
        machineryNormId,
        reason: (reason || "").trim(),
      });
      if (res?.status === true) {
        await fetchMachineryNormItems();
      } else {
        messageApi.error(
          res?.data?.message ||
            res?.message ||
            "Failed to remove the machinery norm",
        );
      }
    } catch (err: any) {
      messageApi.error(err?.message || "Failed to remove the machinery norm");
    } finally {
      setRemovingMachineryId(null);
    }
  };

  // Deployment-norm applications: fetched alongside the machinery norms feed
  // (same trigger, own endpoint). Keeps the WHOLE response — the grand totals
  // and missingRateCount ride on it, not just the items.
  const fetchDeploymentNorms = React.useCallback(async () => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;
    try {
      setDeploymentLoading(true);
      const res = await deploymentNormApis.getTaskNorms(taskId);
      if (res?.status === true) {
        setDeploymentData(res?.data || null);
      }
    } catch {
      /* non-fatal — leave the list as-is */
    } finally {
      setDeploymentLoading(false);
    }
  }, [task?.taskId]);

  useEffect(() => {
    fetchDeploymentNorms();
  }, [fetchDeploymentNorms]);

  // Remove is per-norm, same reason-prompt pattern as the machinery remove.
  const handleRemoveDeployment = async (
    deploymentNormId: number,
    normLabel: string,
  ) => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;

    const { isConfirmed, value: reason } = await Swal.fire({
      title: "Remove equipment norm from task?",
      icon: "warning",
      html: `Removing <b>${normLabel}</b> deletes its equipment-crew requirement on this task. This can't be undone — you'd need to re-apply it to restore.`,
      input: "textarea",
      inputLabel: "Reason for removal",
      inputPlaceholder: "Why is this equipment norm being removed?",
      inputAttributes: { "aria-label": "Reason for removal" },
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      inputValidator: (v: string) =>
        !v || !v.trim() ? "Please enter a reason" : null,
    });
    if (!isConfirmed) return;

    try {
      setRemovingDeploymentId(deploymentNormId);
      const res = await deploymentNormApis.removeFromTask({
        taskId,
        deploymentNormId,
        reason: (reason || "").trim(),
      });
      if (res?.status === true) {
        await fetchDeploymentNorms();
      } else {
        messageApi.error(
          res?.data?.message ||
            res?.message ||
            "Failed to remove the equipment norm",
        );
      }
    } catch (err: any) {
      messageApi.error(err?.message || "Failed to remove the equipment norm");
    } finally {
      setRemovingDeploymentId(null);
    }
  };

  // Resolve tool split-material / sub-material names (backend stores only ids).
  const [materialMap, setMaterialMap] = useState<Record<number, string>>({});
  const [subMaterialMap, setSubMaterialMap] = useState<Record<number, string>>(
    {},
  );
  // Drawer showing a single tool's split-materials.
  const [materialsDrawerTool, setMaterialsDrawerTool] = useState<any | null>(
    null,
  );

  // Whether this task was created with rate analysis. If not, show only Overall
  // (mirrors the "showRateAnalysis" behaviour on the task creation screen).
  const anyRa = (arr: any[]) =>
    (arr || []).some((x: any) => x?.rateAnalysisQty != null);
  const hasRateAnalysis =
    task?.outputQuantity != null ||
    anyRa(task?.taskResources) ||
    anyRa(task?.taskTools) ||
    anyRa(task?.taskBoqs) ||
    (task?.taskTools || []).some((t: any) => anyRa(t?.toolMaterials));

  // Labels mirroring the task creation screen:
  //  "Rate Analysis per 100 SFT per Day" / "Overall (for 1000 SFT)".
  const workUomLabel = task?.workUomName || "";
  const rateAnalysisLabel =
    task?.outputQuantity != null
      ? `Rate Analysis per ${task.outputQuantity}${
          workUomLabel ? ` ${workUomLabel}` : ""
        }${task?.outputBasisUomName ? ` per ${task.outputBasisUomName}` : ""}`
      : "Rate Analysis";
  const overallLabel =
    task?.workQuantity != null
      ? `Overall (for ${task.workQuantity}${
          workUomLabel ? ` ${workUomLabel}` : ""
        })`
      : "Overall";
  useEffect(() => {
    let active = true;
    const tools: any[] = task?.taskTools || [];
    const hasMaterials = tools.some(
      (t: any) => (t.toolMaterials || []).length > 0,
    );
    if (!hasMaterials) return;
    (async () => {
      try {
        const matRes: any = await tasksApis.fetchMachineryMaterialDropDown();
        const mMap: Record<number, string> = {};
        (matRes?.data || []).forEach((m: any) => {
          mMap[m.id ?? m.materialId] = m.materialName ?? m.name ?? m.label;
        });
        const matIds = Array.from(
          new Set(
            tools
              .flatMap((t: any) =>
                (t.toolMaterials || []).map((mm: any) => mm.materialId),
              )
              .filter((id: any) => id != null),
          ),
        );
        const sMap: Record<number, string> = {};
        await Promise.all(
          matIds.map(async (id: any) => {
            try {
              const subRes: any =
                await tasksApis.fetchMachinerySubMaterialDropDown(id);
              (subRes?.data || []).forEach((s: any) => {
                sMap[s.id ?? s.subMaterialId] =
                  s.subMaterialName ?? s.name ?? s.label;
              });
            } catch {
              /* ignore */
            }
          }),
        );
        if (active) {
          setMaterialMap(mMap);
          setSubMaterialMap(sMap);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.taskId]);

  const [subTaskCounts, setSubTaskCounts] = useState<{
    total: number;
    completed: number;
  } | null>(null);

  useEffect(() => {
    let mounted = true;
    const taskId = task?.taskId;
    if (!taskId) {
      setSubTaskCounts(null);
      return;
    }
    (async () => {
      try {
        const response: any = await tasksApis.subTasksList({
          page: 1,
          pageSize: 1000,
          direction: "P",
          sortOrder: "DESC",
          pageDiff: 0,
          paginationId: null,
          projectIds: task?.projectId,
          phaseIds: task?.phaseId,
          taskIds: taskId,
        });
        if (!mounted) return;
        const payload =
          response?.data || response?.response?.data || response?.response;
        const list: any[] = payload?.list || [];
        const total = Number(payload?.count ?? list.length) || 0;
        const completed = list.filter((s: any) => {
          const st = String(s?.subTaskStatus || s?.status || "").toLowerCase();
          return st === "closed" || st === "completed" || st === "approved";
        }).length;
        setSubTaskCounts({ total, completed });
      } catch {
        if (mounted) setSubTaskCounts(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [task?.taskId, task?.projectId, task?.phaseId]);

  // Load task status list once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await tasksApis.fetchTaskStatusList(
          "TaskStatusChange"
        );
        const { data, status } = response || {};
        if (status && mounted) {
          const desiredOrder = [
            "Open",
            "OnHold",
            "InProgress",
            "UnderQC",
            "Closed",
          ];
          const formatted = desiredOrder
            .map((s) => {
              const match = (data || []).find(
                (d: string) => d.toLowerCase() === s.toLowerCase()
              );
              return match ? { label: match, key: match, value: match } : null;
            })
            .filter(Boolean) as { label: string; key: string; value: string }[];
          setStatusList(formatted);
        }
      } catch {
        // silently ignore — dropdown just won't show
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const performStatusChange = async (
    newStatus: string,
    extras?: {
      executedRate?: number;
      rateRemarks?: string;
      taskOnholdReason?: string;
      taskETAResolution?: string;
    }
  ) => {
    if (!task?.taskId) return;
    setStatusUpdating(true);
    try {
      const response = await tasksApis.statusChangeTask(
        task.taskId,
        newStatus,
        extras
      );
      const { status, message: msg, data } = response || {};
      if (status) {
        Swal.fire({
          icon: "success" as SweetAlertIcon,
          title: "Success",
          text:
            data?.message ||
            msg ||
            `Task status updated to ${newStatus} successfully!`,
          confirmButtonText: "Ok",
        });
        dispatch(
          updateTaskListDetailsModalSlice({
            selectedTaskDetails: {
              ...task,
              taskStatus: newStatus,
              ...(extras?.taskOnholdReason
                ? {
                    taskOnholdReason: extras.taskOnholdReason,
                    taskOnholdDate: dayjs().format("YYYY-MM-DD"),
                    taskETAResolution: extras.taskETAResolution,
                  }
                : {}),
            },
          })
        );
        onRefresh?.();
      } else {
        Swal.fire({
          icon: "error" as SweetAlertIcon,
          title: "Error",
          text: msg || "Failed to update status.",
          confirmButtonText: "Ok",
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update status. Please try again.",
        confirmButtonText: "Ok",
      });
    } finally {
      setStatusUpdating(false);
    }
  };

  // Helper to open the OnHold modal (reason + ETA) before holding a task
  const showOnHoldModal = (newStatus: string) => {
    setPendingOnHoldStatus(newStatus);
    setOnHoldReason("");
    setOnHoldETA(null);
    setOnHoldModalVisible(true);
  };

  // Submit handler for OnHold modal
  const handleOnHoldSubmit = async () => {
    if (!onHoldReason || !onHoldReason.trim()) {
      messageApi.open({
        type: "error",
        content: "Please provide a reason for putting the task on hold.",
      });
      return;
    }
    if (!onHoldETA) {
      messageApi.open({
        type: "error",
        content: "Please select an expected resume date.",
      });
      return;
    }
    setOnHoldModalVisible(false);
    await performStatusChange(pendingOnHoldStatus!, {
      taskOnholdReason: onHoldReason.trim(),
      taskETAResolution: onHoldETA.format("YYYY-MM-DD"),
    });
    setPendingOnHoldStatus(null);
    setOnHoldReason("");
    setOnHoldETA(null);
  };

  // Helper to open the Executed Rate modal before closing a task
  const showExecutedRateModal = (newStatus: string) => {
    setPendingClosureStatus(newStatus);
    setExecutedRate(null);
    setRateRemarks("");
    setExecutedRateModalVisible(true);
  };

  // Submit handler for Executed Rate modal
  const handleExecutedRateSubmit = async () => {
    const taskRate = task?.rate || 0;
    if (executedRate === null || executedRate === undefined) {
      messageApi.open({
        type: "error",
        content: "Please enter the executed rate.",
      });
      return;
    }
    const executedRateNum = Number(executedRate);
    if (
      executedRateNum !== taskRate &&
      (!rateRemarks || !rateRemarks.trim())
    ) {
      messageApi.open({
        type: "error",
        content: "Please provide remarks for the rate difference.",
      });
      return;
    }
    setExecutedRateModalVisible(false);
    await performStatusChange(pendingClosureStatus!, {
      executedRate: executedRateNum,
      ...(executedRateNum !== taskRate && rateRemarks?.trim()
        ? { rateRemarks: rateRemarks.trim() }
        : {}),
    });
    setPendingClosureStatus(null);
    setExecutedRate(null);
    setRateRemarks("");
  };

  // Sequentially prompt checklists for the target status (Fill Now / Skip)
  const showSequentialChecklists = async (
    statusesToCheck: string[],
    onComplete: () => Promise<void>
  ) => {
    for (const status of statusesToCheck) {
      const checklist = (task?.checklistLinks || []).find(
        (cl: any) => cl.status === status
      );
      if (checklist && !checklist.answered) {
        await new Promise<void>((resolve) => {
          Modal.confirm({
            icon: null,
            title: (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <span>Checklist Required</span>
                <CloseOutlined
                  style={{
                    fontSize: 18,
                    cursor: "pointer",
                    marginRight: -8,
                    color: "#888",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof (Modal as any).destroyAll === "function") {
                      (Modal as any).destroyAll();
                    }
                  }}
                />
              </div>
            ),
            content: (
              <div style={{ marginTop: 16 }}>
                <p>
                  To change the status to{" "}
                  <strong style={{ color: "#2557d6", fontWeight: 600 }}>
                    {status}
                  </strong>{" "}
                  you need to complete the checklist:{" "}
                  <strong style={{ color: "#2557d6", fontWeight: 500 }}>
                    {checklist.checklistName}
                  </strong>
                </p>
                <p>
                  Would you like to fill the checklist now or skip it for later?
                </p>
              </div>
            ),
            width: 500,
            okText: "Fill Now",
            cancelText: "Skip for Later",
            okButtonProps: { type: "primary" },
            onOk: () => {
              setSelectedChecklistForModal({
                checklistId: checklist.checklistId,
                checklistLinkId: checklist.checklistLinkId,
              });
              setShowChecklistModal(true);
              pendingStatusRef.current = status;
              // Intentionally not resolving — handleChecklistModalClose will
              // continue the flow after the checklist is saved.
            },
            onCancel: () => {
              resolve();
            },
          });
        });
      }
    }
    await onComplete();
  };

  // Handler invoked when the checklist modal closes (after save)
  const handleChecklistModalClose = async (
    action: "skip" | "save",
    checklistSuccess?: boolean
  ) => {
    setShowChecklistModal(false);
    const statusToSet = pendingStatusRef.current;
    if (action === "save" && checklistSuccess === false) {
      pendingStatusRef.current = null;
      return;
    }
    if (statusToSet) {
      // Refresh task data so the newly answered checklist is reflected
      onRefresh?.();
      if (statusToSet.toLowerCase() === "closed") {
        // For Closed, validate completion% then prompt executed rate
        const completion = task?.taskCompletionPercentage || 0;
        if (Math.round(completion) !== 100) {
          pendingStatusRef.current = null;
          Swal.fire({
            icon: "warning",
            title: "Cannot Close Task",
            text: `Task completion percentage must be 100% to close the task. Current: ${Math.round(
              completion
            )}%`,
            confirmButtonText: "Ok",
          });
          return;
        }
        pendingStatusRef.current = null;
        showExecutedRateModal(statusToSet);
        return;
      }
      pendingStatusRef.current = null;
      await performStatusChange(statusToSet);
    }
  };

  const [isApprovalProcessing, setIsApprovalProcessing] = useState(false);

  const handleApproveReject = async (
    action: "approve" | "reject",
    reason?: string
  ) => {
    if (!task?.approvalLinkId) return;
    setIsApprovalProcessing(true);
    try {
      const baseRequestData = {
        projectId: task.projectId,
        data: [
          {
            approvalLinkId: task.approvalLinkId,
            parentModule: "Task",
          },
        ],
      };
      const response: any =
        action === "approve"
          ? await workApprovalApis.approveWorkItem(baseRequestData)
          : await workApprovalApis.rejectWorkItem({
              ...baseRequestData,
              reason: reason || "",
            });
      const { status, message: msg } = response || {};
      if (status) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response?.data?.message ||
            msg ||
            `Successfully ${action === "approve" ? "approved" : "rejected"} task`,
          confirmButtonText: "OK",
        });
        onRefresh?.();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text:
            response?.data?.message ||
            response?.response?.data?.message ||
            msg ||
            `Failed to ${action} item. Please try again.`,
          confirmButtonText: "OK",
        });
      }
    } catch (err: any) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          `Failed to ${action} item.`,
        confirmButtonText: "OK",
      });
    } finally {
      setIsApprovalProcessing(false);
    }
  };

  const promptReject = async () => {
    const { value: reason } = await Swal.fire({
      title: "Reject Approval",
      input: "textarea",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "Type your reason here...",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) return "Please input your reason!";
        return null;
      },
    });
    if (reason) handleApproveReject("reject", reason);
  };

  const handleStatusMenuClick = async ({ key }: { key: string }) => {
    const newStatus = key;
    if (!newStatus || newStatus === task?.taskStatus) return;

    const currentStatus = task?.taskStatus;
    const statusOrder = [
      "Open",
      "Active",
      "InProgress",
      "Resolved",
      "UnderQC",
      "Closed",
    ];
    const currentStatusIndex = statusOrder.findIndex(
      (s) => s.toLowerCase() === (currentStatus || "").toLowerCase()
    );
    const newStatusIndex = statusOrder.findIndex(
      (s) => s.toLowerCase() === (newStatus || "").toLowerCase()
    );

    // OnHold transitions need reason + ETA — capture inline via modal
    if (
      newStatus.toLowerCase() === "onhold" &&
      ["inprogress", "underqc", "active", "open"].includes(
        (currentStatus || "").toLowerCase()
      )
    ) {
      showOnHoldModal(newStatus);
      return;
    }

    // OnHold → InProgress / UnderQC: prompt sequential checklists
    if (
      currentStatus === "OnHold" &&
      (newStatus === "InProgress" || newStatus === "UnderQC")
    ) {
      await showSequentialChecklists([newStatus], () =>
        performStatusChange(newStatus)
      );
      return;
    }

    // UnderQC → Closed: special validation + checklist + executed rate
    if (currentStatus === "UnderQC" && newStatus === "Closed") {
      const completion = task?.taskCompletionPercentage || 0;
      if (Math.round(completion) !== 100) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Close Task",
          text: `Task completion percentage must be 100% to close the task. Current: ${Math.round(
            completion
          )}%`,
          confirmButtonText: "Ok",
        });
        return;
      }

      const inProgressChecklist = (task?.checklistLinks || []).find(
        (cl: any) => cl.status === "InProgress"
      );
      const underQCChecklist = (task?.checklistLinks || []).find(
        (cl: any) => cl.status === "UnderQC"
      );
      const closedChecklist = (task?.checklistLinks || []).find(
        (cl: any) => cl.status === "Closed"
      );

      if (closedChecklist) {
        const hasUnfilledPrevious =
          (inProgressChecklist && !inProgressChecklist.answered) ||
          (underQCChecklist && !underQCChecklist.answered);
        if (hasUnfilledPrevious) {
          Swal.fire({
            icon: "warning",
            title: "Please fill required checklist(s) below.",
            text: "You must fill all previous checklists before closing.",
            confirmButtonText: "Ok",
          });
          return;
        }
        if (closedChecklist.answered) {
          showExecutedRateModal(newStatus);
          return;
        }
        // Closed checklist exists but unfilled — prompt to fill (no skip)
        Modal.confirm({
          icon: null,
          title: (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <span>Checklist Required</span>
              <CloseOutlined
                style={{
                  fontSize: 18,
                  cursor: "pointer",
                  marginRight: -8,
                  color: "#888",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof (Modal as any).destroyAll === "function") {
                    (Modal as any).destroyAll();
                  }
                }}
              />
            </div>
          ),
          content: (
            <div style={{ marginTop: 16 }}>
              <p>
                To change the status to{" "}
                <strong style={{ color: "#2557d6", fontWeight: 600 }}>
                  Closed
                </strong>{" "}
                you need to complete the checklist:{" "}
                <strong style={{ color: "#2557d6", fontWeight: 500 }}>
                  {closedChecklist.checklistName}
                </strong>
              </p>
            </div>
          ),
          width: 500,
          okText: "Fill Now",
          cancelButtonProps: { style: { display: "none" } },
          okButtonProps: { type: "primary" },
          onOk: () => {
            setSelectedChecklistForModal({
              checklistId: closedChecklist.checklistId,
              checklistLinkId: closedChecklist.checklistLinkId,
            });
            setShowChecklistModal(true);
            pendingStatusRef.current = newStatus;
          },
        });
        return;
      }
      // No Closed checklist defined — proceed to executed rate
      showExecutedRateModal(newStatus);
      return;
    }

    // Open → Active / InProgress, Active → InProgress, InProgress → UnderQC: sequential checklists
    if (
      (currentStatusIndex === 0 && newStatusIndex === 1) ||
      (currentStatusIndex === 0 && newStatusIndex === 2) ||
      (currentStatusIndex === 1 && newStatusIndex === 2) ||
      (currentStatusIndex === 2 && newStatusIndex === 4)
    ) {
      await showSequentialChecklists([newStatus], () =>
        performStatusChange(newStatus)
      );
      return;
    }

    // Any other → Closed: validate 100% then prompt executed rate
    if (newStatus.toLowerCase() === "closed") {
      const completion = task?.taskCompletionPercentage || 0;
      if (Math.round(completion) !== 100) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Close Task",
          text: `Task completion percentage must be 100% to close the task. Current: ${Math.round(
            completion
          )}%`,
          confirmButtonText: "Ok",
        });
        return;
      }
      showExecutedRateModal(newStatus);
      return;
    }

    // Default: simple confirm + status change
    Swal.fire({
      title: `Change status to ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        performStatusChange(newStatus);
      }
    });
  };

  if (!task) {
    return (
      <div style={{ padding: 40 }}>
        <Empty description="No task data available" />
      </div>
    );
  }

  // Permission check (mirrors TaskInformationById's status-change gating)
  const projectStatus = (task.projectStatus || "").toLowerCase();
  const phaseStatus = (task.phaseStatus || "").toLowerCase();
  const activityStatus = (task.activityStatus || "").toLowerCase();
  const blocked = ["onhold", "cancelled", "archived", "closed"];
  const baseConditionsMet =
    !blocked.includes(projectStatus) &&
    !blocked.includes(phaseStatus) &&
    !["onhold", "cancelled", "archived", "closed", "deleted"].includes(
      activityStatus
    );
  const isInHierarchy =
    Array.isArray(task.hierarchyUserIds) &&
    !!currentUser?.id &&
    task.hierarchyUserIds.filter((id: number) => id > 0).includes(currentUser.id);
  const isAdmin = (currentUser?.genericRole || "").toLowerCase() === "admin";
  const isTaskOwner = task.taskOwnerId === currentUser?.id;
  const statusChangeAllowed = !["closed", "archived"].includes(
    (task.taskStatus || "").toLowerCase()
  );
  // Backend authoritative gate: when allowTaskUpdates is explicitly false,
  // the user cannot change status regardless of role/hierarchy.
  const allowTaskUpdates = task?.allowTaskUpdates !== false;
  const canChangeStatus =
    allowTaskUpdates &&
    baseConditionsMet &&
    statusChangeAllowed &&
    (isTaskOwner || isAdmin || isInHierarchy);

  // Apply Norm (server-side attach) is offered when the task is editable —
  // same base/owner gate as status change, but excludes on-hold tasks.
  const canApplyNorm =
    allowTaskUpdates &&
    baseConditionsMet &&
    !["closed", "archived", "onhold"].includes(
      (task.taskStatus || "").toLowerCase()
    ) &&
    (isTaskOwner || isAdmin || isInHierarchy);

  const completionPercent = parseFloat(
    task.completionPercentage || task.taskCompletionPercentage || "0"
  );

  const subTasksList = Array.isArray(task.subTasks) ? task.subTasks : [];
  const fallbackTotal =
    task.totalSubTasks ?? task.subTaskCount ?? subTasksList.length;
  const fallbackCompleted =
    task.completedSubTasks ??
    task.completedSubTaskCount ??
    subTasksList.filter((s: any) => {
      const st = String(s.subTaskStatus || s.status || "").toLowerCase();
      return st === "closed" || st === "completed" || st === "approved";
    }).length;
  const totalSubtasks = subTaskCounts?.total ?? fallbackTotal;
  const completedSubtasks = subTaskCounts?.completed ?? fallbackCompleted;

  const resourceCount = (task.taskResources || []).length;
  const userCount = (task.taskUsers || []).length;
  const toolCount = (task.taskTools || []).length;
  const boqCount = (task.taskBoqs || []).length;

  const isOnHold = (task.taskStatus || "").toLowerCase() === "onhold";
  const isReassigned =
    task.originalTaskOwnerId &&
    task.originalTaskOwnerId !== task.taskOwnerId;
  const hasAutoApprovalRemarks =
    task.approvalRequired === false && !!task.autoApprovalRemarks;

  const isApprover =
    !!task.approvalLinkId &&
    Array.isArray(task.stateApprovals) &&
    (task.stateApprovals.some(
      (a: any) => a.userId === currentUser?.id && a.status === "Pending"
    ) ||
      (isAdmin &&
        task.stateApprovals.some((a: any) => a.status === "Pending")));

  const approvalChain: any[] = Array.isArray(task.stateApprovalHierarchy)
    ? task.stateApprovalHierarchy
    : [];

  const ApprovalHierarchyPopover = () => (
    <Popover
      trigger={["hover", "click"]}
      placement="right"
      title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
      content={
        <Timeline className="approval-timeline">
          {approvalChain.map((approval: any, index: number) => {
            const status =
              approval.approvalStatus || approval.status;
            const isApproved = status === "Approved";
            const isRejected = status === "Rejected";
            return (
              <Timeline.Item
                key={approval.stateApprovalHierarchyId ?? index}
                dot={
                  isApproved ? (
                    <CheckCircleOutlined style={{ color: "green" }} />
                  ) : isRejected ? (
                    <CloseCircleOutlined style={{ color: "red" }} />
                  ) : undefined
                }
              >
                {approval?.userName}
                <div className="designation">{approval?.roleName}</div>
                {approval?.approvalStage && (
                  <div className="designation">{approval?.approvalStage}</div>
                )}
                {approval?.level && (
                  <div className="designation">Level: {approval?.level}</div>
                )}
                {status && status !== "Active" && (
                  <div className="designation">Status: {status}</div>
                )}
                {approval?.actionDate && (
                  <div className="designation">
                    {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                  </div>
                )}
              </Timeline.Item>
            );
          })}
        </Timeline>
      }
    >
      <span
        style={{
          color: "#2557d6",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        Yes
      </span>
    </Popover>
  );

  // Pending status change pushed by other modules (e.g. a Timelog edit that
  // asks the task to move to Closed). Shown inline next to the current
  // status as `Current → New`.
  const pendingUpdates = (task as any).updates;
  const hasPendingStatusChange =
    !!pendingUpdates?.taskStatus &&
    String(pendingUpdates.taskStatus).toLowerCase() !==
      String(task.taskStatus || "").toLowerCase();

  return (
    <div style={{ padding: "20px 24px" }}>
      {msgContext}
      {/* Status & quick action row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            rowGap: 6,
            columnGap: 8,
          }}
        >
          {canChangeStatus && statusList.length > 0 ? (
            <Dropdown
              menu={{
                items: statusList,
                selectedKeys: [task.taskStatus],
                onClick: handleStatusMenuClick,
              }}
              trigger={["click"]}
              placement="bottomLeft"
              disabled={statusUpdating}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: statusUpdating ? "wait" : "pointer",
                }}
                title="Click to change status"
              >
                <GenericStatusPill
                  status={task.taskStatus}
                  label={task.taskStatus || "N/A"}
                />
                <DownOutlined style={{ fontSize: 9, color: "#666" }} />
              </span>
            </Dropdown>
          ) : (
            <GenericStatusPill
              status={task.taskStatus}
              label={task.taskStatus || "N/A"}
            />
          )}
          {hasPendingStatusChange && (
            <>
              <ArrowRightOutlined
                style={{ color: "#999", fontSize: 11 }}
              />
              <span title="Pending status change">
                <GenericStatusPill status={pendingUpdates.taskStatus} />
              </span>
            </>
          )}
          {task.priorityName && <PriorityPill priority={task.priorityName} />}
          {task.workCategoryName && (
            <Tag color="purple" style={{ margin: 0 }}>
              {task.workCategoryName}
            </Tag>
          )}
          {task.isBillable !== undefined && (
            <Tag color={task.isBillable ? "green" : "default"} style={{ margin: 0 }}>
              {task.isBillable ? "Billable" : "Non-Billable"}
            </Tag>
          )}
          {task.workType && task.workType !== "Open" && (
            <Tag color="cyan" style={{ margin: 0 }}>
              {task.workType}
            </Tag>
          )}
          {task.approvalRequired === false && (
            <Tag color="gold" icon={<SafetyCertificateOutlined />} style={{ margin: 0 }}>
              No Approval
            </Tag>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {isApprover && (
            <>
              <Button
                size="small"
                style={{
                  backgroundColor: statusTone("Approved").bg,
                  color: statusTone("Approved").text,
                  borderColor: statusTone("Approved").bg,
                  fontWeight: 500,
                }}
                loading={isApprovalProcessing}
                onClick={() => handleApproveReject("approve")}
              >
                Approve
              </Button>
              <Button
                size="small"
                style={{
                  backgroundColor: statusTone("Rejected").bg,
                  color: statusTone("Rejected").text,
                  borderColor: statusTone("Rejected").bg,
                  fontWeight: 500,
                }}
                loading={isApprovalProcessing}
                onClick={promptReject}
              >
                Reject
              </Button>
            </>
          )}
          {showEditButton &&
            !["closed", "archived", "onhold", "cancelled", "deleted"].includes(
              (task?.taskStatus || "").toLowerCase()
            ) && (
              <Button
                type="primary"
                ghost
                size="small"
                icon={<EditOutlined />}
                onClick={onEdit}
              >
                Edit
              </Button>
            )}
        </div>
      </div>

      {/* Task Details */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            Task Details
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
        headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <Descriptions
          column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          size="small"
          labelStyle={{
            fontWeight: 500,
            color: "#666",
            width: 150,
            whiteSpace: "nowrap",
          }}
        >
          <Descriptions.Item
            label={
              <>
                <UserOutlined style={{ marginRight: 6 }} />
                Owner
              </>
            }
          >
            {task.taskOwnerName ? (
              <Tag color="green" style={{ borderRadius: 12, padding: "1px 10px" }}>
                <UserOutlined style={{ marginRight: 4 }} />
                {task.taskOwnerName}
              </Tag>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Planned Start
              </>
            }
          >
            {formatDate(task.taskPlannedStartDate)}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Planned End
              </>
            }
          >
            {formatDate(task.taskPlannedEndDate)}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Actual Start
              </>
            }
          >
            {task.taskActualStartDate
              ? dayjs(task.taskActualStartDate).format("DD MMM YYYY")
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Actual End
              </>
            }
          >
            {task.taskActualEndDate
              ? dayjs(task.taskActualEndDate).format("DD MMM YYYY")
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <BellOutlined style={{ marginRight: 6 }} />
                Reminder
              </>
            }
          >
            {(() => {
              const reminder = task?.reminders;
              if (
                !reminder ||
                !reminder.reminderType ||
                !reminder.specificTime
              ) {
                return "N/A";
              }
              let text = reminder.reminderType;
              if (
                reminder.reminderType === "On" &&
                reminder.specificDate
              ) {
                text += ` ${dayjs(reminder.specificDate).format(
                  "MMM DD, YYYY"
                )}`;
              } else if (
                reminder.reminderType !== "Daily" &&
                reminder.offsetFrom
              ) {
                text += ` ${reminder.offsetFrom}`;
              }
              text += ` ${dayjs(reminder.specificTime, "HH:mm").format(
                "HH:mm"
              )}`;
              return text;
            })()}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <ReloadOutlined style={{ marginRight: 6 }} />
                Recurrence
              </>
            }
          >
            {task?.reminders?.recurrenceType
              ? formatRecurrence(task.reminders)
              : "N/A"}
          </Descriptions.Item>
          {task.workType === "Quantity" && (
            <Descriptions.Item
              label={
                <>
                  <PercentageOutlined style={{ marginRight: 6 }} />
                  Work Quantity
                </>
              }
            >
              <Tag color="geekblue" style={{ borderRadius: 12 }}>
                {task.workQuantity ?? "N/A"}
                {task.workUomShortCode || task.workUomName
                  ? ` ${task.workUomShortCode || task.workUomName}`
                  : ""}
              </Tag>
            </Descriptions.Item>
          )}
          {(task.rate !== undefined && task.rate !== null) && (
            <Descriptions.Item
              label={
                <>
                  <DollarOutlined style={{ marginRight: 6 }} />
                  Rate incl. GST
                </>
              }
            >
              {/* Hover shows how the quoted rate resolves to the Basic Rate. */}
              <Tooltip
                title={rateInclGstTooltip(
                  computeTaskPriceBreakdown(
                    task.rate,
                    task.gstPercent ?? DEFAULT_GST_PERCENT,
                    task.cessPercent ?? DEFAULT_CESS_PERCENT,
                  ),
                  Number(task.gstPercent ?? DEFAULT_GST_PERCENT),
                  Number(task.cessPercent ?? DEFAULT_CESS_PERCENT),
                )}
              >
                <span
                  style={{
                    cursor: "help",
                    borderBottom: "1px dotted #8094ae",
                  }}
                >
                  {fmtAmount(Number(task.rate) || 0)}
                </span>
              </Tooltip>
            </Descriptions.Item>
          )}
          {/* The working rate — what every costing / P&L report is based on. */}
          {(task.uploadRate !== undefined && task.uploadRate !== null) && (
            <Descriptions.Item
              label={
                <>
                  <DollarOutlined style={{ marginRight: 6 }} />
                  Basic Rate
                </>
              }
            >
              {fmtAmount(Number(task.uploadRate) || 0)}
            </Descriptions.Item>
          )}
          {(task.allocatedBudgetAmount !== undefined &&
            task.allocatedBudgetAmount !== null) && (
            <Descriptions.Item
              label={
                <>
                  <DollarOutlined style={{ marginRight: 6 }} />
                  Allocated Budget
                </>
              }
            >
              <Tag color="green">
                {fmtAmount(Number(task.allocatedBudgetAmount) || 0)}
              </Tag>
            </Descriptions.Item>
          )}
          {(task.siteName ||
            task.siteStructureUnitName ||
            task.floorNo ||
            task.siteStructureSubUnitName ||
            task.locationName) && (
            <Descriptions.Item
              label={
                <>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />
                  Location
                </>
              }
              span={3}
            >
              {[
                task.siteName,
                task.siteStructureUnitName,
                task.floorNo,
                task.siteStructureSubUnitName,
                task.locationName,
              ]
                .filter(Boolean)
                .join(" / ") || "N/A"}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={
              <>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                Created
              </>
            }
          >
            {(() => {
              const createdDate =
                task.createdAt ||
                task.taskCreatedAt ||
                task.createdOn ||
                task.createdDate ||
                task.createdDateTime;
              const createdBy =
                task.createdByName ||
                task.createdUserName ||
                task.createdByUserName;
              if (!createdDate && !createdBy) return "N/A";
              return (
                <>
                  {createdDate && dayjs(createdDate).format("DD MMM YYYY")}
                  {createdBy && (
                    <span
                      style={{
                        color: "#999",
                        marginLeft: createdDate ? 8 : 0,
                      }}
                    >
                      {createdDate ? "by " : ""}
                      {createdBy}
                    </span>
                  )}
                </>
              );
            })()}
          </Descriptions.Item>
          {task.approvalRequired !== undefined && (
            <Descriptions.Item
              label={
                <>
                  <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                  Approval Required
                </>
              }
            >
              {task.approvalRequired && approvalChain.length > 0 ? (
                <ApprovalHierarchyPopover />
              ) : (
                <Tag color={task.approvalRequired ? "blue" : "default"}>
                  {task.approvalRequired ? "Yes" : "No"}
                </Tag>
              )}
            </Descriptions.Item>
          )}
          {task.taskSequenceId && (
            <Descriptions.Item
              label={
                <>
                  <NumberOutlined style={{ marginRight: 6 }} />
                  Task ID
                </>
              }
            >
              <span style={{ fontFamily: "monospace", color: "#1a1a2e" }}>
                {task.taskSequenceId}
              </span>
            </Descriptions.Item>
          )}
          {task.internalTaskNumber && (
            <Descriptions.Item
              label={
                <>
                  <NumberOutlined style={{ marginRight: 6 }} />
                  Internal Task No.
                </>
              }
            >
              <span style={{ fontFamily: "monospace", color: "#1a1a2e" }}>
                {task.internalTaskNumber}
              </span>
            </Descriptions.Item>
          )}
          {(task.budgetLineName || task.budgetHeaderName) && (
            <Descriptions.Item
              label={
                <>
                  <DollarOutlined style={{ marginRight: 6 }} />
                  Budget
                </>
              }
              span={3}
            >
              {task.budgetHeaderName && (
                <span style={{ color: "#6b7280" }}>
                  {task.budgetHeaderName} /{" "}
                </span>
              )}
              <span style={{ fontWeight: 500 }}>
                {task.budgetLineName || "N/A"}
              </span>
              {task.budgetValue !== undefined && task.budgetValue !== null && (
                <Tag color="green" style={{ marginLeft: 8 }}>
                  {fmtAmount(Number(task.budgetValue) || 0)}
                </Tag>
              )}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Description */}
      {task.taskDescription && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              Description
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div
            style={{
              fontSize: 13,
              color: "#4b5563",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {task.taskDescription}
          </div>
        </Card>
      )}

      {/* Tags */}
      {task.taskTags && task.taskTags.length > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <TagsOutlined style={{ marginRight: 8 }} />
              Tags
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {task.taskTags.map((tag: any, index: number) => {
              const color = getColorByLabel(tag.tagName || "");
              return (
                <Tag
                  key={index}
                  style={{
                    backgroundColor: color,
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 10px",
                  }}
                >
                  {tag.tagName}
                </Tag>
              );
            })}
          </div>
        </Card>
      )}

      {/* No-Approval Remarks */}
      {hasAutoApprovalRemarks && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#d48806" }}>
              <SafetyCertificateOutlined style={{ marginRight: 8 }} />
              No-Approval Remarks
            </span>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            background: "#fffbe6",
            border: "1px solid #ffe58f",
          }}
          headStyle={{
            borderBottom: "1px solid #ffe58f",
            minHeight: 40,
            background: "#fffbe6",
          }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={{ fontSize: 13, color: "#664d03", lineHeight: 1.6 }}>
            {task.autoApprovalRemarks}
          </div>
        </Card>
      )}

      {/* On Hold Info */}
      {isOnHold && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#d48806" }}>
              On Hold Information
            </span>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            background: "#fffbe6",
            border: "1px solid #ffe58f",
          }}
          headStyle={{
            borderBottom: "1px solid #ffe58f",
            minHeight: 40,
            background: "#fffbe6",
          }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Timeline
            items={[
              {
                color: "orange",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Hold Date</div>
                    <div style={{ color: "#666" }}>
                      {formatDate(task.taskOnholdDate)}
                    </div>
                  </>
                ),
              },
              {
                color: "green",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Expected Resolution</div>
                    <div style={{ color: "#666" }}>
                      {formatDate(task.taskETAResolution)}
                    </div>
                  </>
                ),
              },
              {
                color: "blue",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Reason</div>
                    <div style={{ color: "#666" }}>
                      {task.taskOnholdReason || "No reason provided"}
                    </div>
                  </>
                ),
              },
            ]}
          />
        </Card>
      )}

      {/* Owner Reassignment */}
      {isReassigned && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1890ff" }}>
              Owner Reassignment
            </span>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            background: "#e6f7ff",
            border: "1px solid #91d5ff",
          }}
          headStyle={{
            borderBottom: "1px solid #91d5ff",
            minHeight: 40,
            background: "#e6f7ff",
          }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Descriptions
            column={1}
            size="small"
            labelStyle={{ fontWeight: 500, color: "#666" }}
          >
            <Descriptions.Item label="Original Owner">
              {task.originalTaskOwnerName || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Reason">
              {task.reassignedReason || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Progress Section */}
      <SectionCard style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
            <div>
              <div className="pms-eyebrow">Subtasks Completed</div>
              <div className="pms-stat-value">
                {completedSubtasks}
                <span
                  style={{ color: "#8094ae", fontSize: 14, fontWeight: 400 }}
                >
                  {" "}
                  / {totalSubtasks}
                </span>
              </div>
            </div>
            {task.taskWeightagePercentage && (
              <div>
                <div className="pms-eyebrow">Weightage</div>
                <div className="pms-stat-value">
                  {task.taskWeightagePercentage}
                  <span
                    style={{
                      color: "#8094ae",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    %
                  </span>
                </div>
              </div>
            )}
          </div>
          <Progress
            type="circle"
            percent={completionPercent}
            size={56}
            strokeWidth={8}
            strokeColor="#52c41a"
            trailColor="#f0f0f0"
            format={(percent) => (
              <span
                style={{ color: "#1a1a2e", fontWeight: 600, fontSize: 13 }}
              >
                {percent?.toFixed(0)}%
              </span>
            )}
          />
        </div>
        {/* Budget progress: actual 3M consumption from approved time logs */}
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid #f5f5f5",
          }}
        >
          <div
            style={{
              color: "#8c8c8c",
              fontSize: 11,
              marginBottom: 8,
            }}
          >
            Budget Progress (3M Consumption)
          </div>
          <Row gutter={16}>
            {[
              {
                label: "Material",
                value: task.materialConsumption,
                planned: task.materialPlanned,
                color: "#52c41a",
              },
              {
                label: "Machinery",
                value: task.machineryConsumption,
                planned: task.machineryPlanned,
                color: "#fa8c16",
              },
              {
                label: "Manpower",
                value: task.manpowerConsumption,
                planned: task.manpowerPlanned,
                color: "#1677ff",
              },
              {
                label: "Total",
                value: task.totalConsumption,
                planned: task.totalPlanned,
                color: "#722ed1",
              },
            ].map(({ label, value, planned, color }) => {
              const consumed = Number(value) || 0;
              const allocated = Number(planned) || 0;
              const fmt = (n: number) =>
                `₹${n.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}`;
              const percent =
                allocated > 0
                  ? Math.round((consumed / allocated) * 100)
                  : 0;
              // consumed with no allocation counts as overrun (bar shows full red)
              const barPercent =
                allocated > 0
                  ? Math.min(100, percent)
                  : consumed > 0
                  ? 100
                  : 0;
              const isOverrun =
                (allocated > 0 && percent > 100) ||
                (allocated <= 0 && consumed > 0);
              return (
                <Col span={6} key={label}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 11,
                    }}
                  >
                    <span
                      style={{
                        color: "#595959",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: isOverrun ? "#ff4d4f" : color,
                          display: "inline-block",
                        }}
                      />
                      {label}
                    </span>
                    {allocated > 0 && (
                      <span
                        style={{
                          color: isOverrun ? "#ff4d4f" : "#8c8c8c",
                          fontWeight: 500,
                        }}
                      >
                        {percent}%
                      </span>
                    )}
                  </div>
                  <Progress
                    percent={barPercent}
                    showInfo={false}
                    strokeLinecap="round"
                    size={["100%", 5]}
                    strokeColor={isOverrun ? "#ff4d4f" : color}
                    trailColor="#f0f0f0"
                    style={{ margin: "2px 0" }}
                  />
                  <div style={{ fontSize: 12 }}>
                    <span style={{ color: "#1a1a2e", fontWeight: 600 }}>
                      {fmt(consumed)}
                    </span>
                    <span style={{ color: "#8c8c8c", fontSize: 11 }}>
                      {" "}
                      / {allocated > 0 ? fmt(allocated) : "Not allocated"}
                    </span>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </SectionCard>

      {/* 3M summary cards + drill-down drawer (Material / Manpower / Machinery) */}
      {(() => {
        const workQty = Number(task.workQuantity) || 0;
        const chipStyle = (color: string, tint: string): React.CSSProperties => ({
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: tint,
          border: `1px solid ${color}`,
          borderRadius: 14,
          padding: "2px 10px",
          fontSize: 12,
          color,
          fontWeight: 600,
        });
        const removeXStyle = (busy: boolean): React.CSSProperties => ({
          cursor: busy ? "default" : "pointer",
          color: "#c0392b",
          fontWeight: 700,
          lineHeight: 1,
          marginLeft: 2,
          opacity: busy ? 0.4 : 1,
        });
        const sectionLabelStyle: React.CSSProperties = {
          fontSize: 12,
          fontWeight: 600,
          color: "#022c57",
          margin: "4px 0 6px",
        };
        const drawerFootRow: React.CSSProperties = {
          display: "flex",
          justifyContent: "space-between",
          fontSize: 13,
          color: "#526484",
          padding: "2px 0",
          fontVariantNumeric: "tabular-nums",
        };
        const drawerFootTotal: React.CSSProperties = {
          fontWeight: 700,
          color: "#022c57",
          fontSize: 15,
          borderTop: "1px solid #e4e9f0",
          marginTop: 4,
          paddingTop: 6,
        };

        // Material = BOQ + norm-material requirement
        const materialNormRows = normGroups.flatMap((g: any) =>
          (g.materials || []).map((m: any) => ({
            ...m,
            normCode: g.normCode,
            normTitle: g.normTitle,
          })),
        );
        const materialNormTotal = materialNormRows.reduce(
          (s: number, m: any) =>
            s + Number(m.appliedQuantity || 0) * (Number(m.appliedRate) || 0),
          0,
        );
        const materialBoqTotal = sumAmount(task.taskBoqs);
        const materialTotal = materialBoqTotal + materialNormTotal;

        // Manpower = resources + PRW norm requirement
        const manpowerResTotal = sumAmount(task.taskResources);
        const manpowerNormTotal = manpowerItems.reduce(
          (s: number, m: any) =>
            s + Number(m.appliedQuantity || 0) * (Number(m.appliedRate) || 0),
          0,
        );
        const manpowerTotal = manpowerResTotal + manpowerNormTotal;

        // Machinery = tools + machinery norm requirement + equipment norms
        // (the equipment share is LIVE-derived — hoisted here so the total
        // can include it; per user decision it JOINS Machinery Total).
        const machineryToolsTotal = sumAmount(task.taskTools);
        const machineryNormTotal = machineryNormItems.reduce(
          (s: number, m: any) =>
            s + Number(m.appliedQuantity || 0) * (Number(m.appliedRate) || 0),
          0,
        );
        const deploymentItems: any[] = Array.isArray(deploymentData?.items)
          ? deploymentData.items
          : [];
        const deploymentTotalHours = Number(deploymentData?.totalHours) || 0;
        const deploymentTotalAmount = Number(deploymentData?.totalAmount) || 0;
        const machineryTotal =
          machineryToolsTotal + machineryNormTotal + deploymentTotalAmount;

        // Machinery norm rows grouped by head. The drawer shows a row for
        // EVERY head (all 9 — heads with no norms read ₹0), plus any head that
        // has data but isn't in the head list (defensive). Each carries its
        // own subtotal + live flag (coming-soon heads can't be applied yet).
        const machineryNormGroups = (() => {
          const byHead = new Map<string, any[]>();
          for (const m of machineryNormItems) {
            const head = m.category || "—";
            if (!byHead.has(head)) byHead.set(head, []);
            byHead.get(head)!.push(m);
          }
          const allKeys = MACHINERY_HEADS.map((h) => h.key);
          const extraKeys = Array.from(byHead.keys()).filter(
            (k) => !allKeys.includes(k),
          );
          const liveKeys = MACHINERY_HEADS.filter((h) => h.live).map(
            (h) => h.key,
          );
          return [...allKeys, ...extraKeys].map((head) => {
            const items = byHead.get(head) || [];
            const subtotal = items.reduce(
              (s: number, m: any) =>
                s +
                Number(m.appliedQuantity || 0) * (Number(m.appliedRate) || 0),
              0,
            );
            // extras (data head not in the list) are treated as live so they
            // remain actionable.
            const live = liveKeys.includes(head) || !allKeys.includes(head);
            return { key: head, head, items, subtotal, live };
          });
        })();

        // Shared column renderers for the grouped "From Norm" machinery lists.
        // The head is shown in each group's sub-header, so the row name no
        // longer repeats the head tag.
        const machineryNormGetName = (m: any) => {
          const leadDump = [
            m.totalLead != null && m.totalLead !== ""
              ? `Lead: ${m.totalLead}`
              : "",
            m.nearestDump ? `Dump: ${m.nearestDump}` : "",
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <span>
              <span style={{ fontWeight: 500, color: "#1a1a2e" }}>
                {m.normCode}
              </span>
              {(m.description || leadDump) && (
                <div style={{ color: "#9ca3af", fontSize: 11 }}>
                  {m.description}
                  {m.description && leadDump ? " · " : ""}
                  {leadDump}
                </div>
              )}
            </span>
          );
        };
        // Machinery has NO stored per-unit factor (applied qty = task work qty,
        // or an override) — derive it: applied ÷ work qty per 1 work UOM,
        // priced at the applied rate.
        const machineryNormGetRa = (m: any) => {
          const wq = Number(task?.workQuantity) || 0;
          if (!wq) return null;
          const perUnitQty = Number(
            (Number(m.appliedQuantity || 0) / wq).toFixed(4),
          );
          const rate = Number(m.appliedRate) || 0;
          return {
            qty: perUnitQty,
            uom: m.uomName,
            rate,
            total: perUnitQty * rate,
          };
        };
        const machineryNormGetOverall = (m: any) => ({
          qty: m.appliedQuantity,
          uom: m.uomName,
          rate: Number(m.appliedRate) || 0,
          total:
            Number(m.appliedQuantity || 0) * (Number(m.appliedRate) || 0),
        });

        // Deployment norms — the parallel LIVE-derived block. Decimals arrive
        // as strings ('' possible on optionals). deploymentItems/totals are
        // hoisted above so the equipment share joins machineryTotal.
        const deploymentMissingRates =
          Number(deploymentData?.missingRateCount) || 0;
        const deploymentAnyNoVersion = deploymentItems.some(
          (i: any) => !!i.noVersion,
        );
        const deploymentNoRateCard = deploymentItems.some(
          (i: any) => (i.ratesResolvedFrom || "") === "NONE",
        );
        // Factors are fractions on the wire (0.05 = 5%) — show whole percents.
        const fmtDeployPct = (v: any) => {
          if (v === null || v === undefined || v === "") return "—";
          const n = Number(v);
          if (!Number.isFinite(n)) return "—";
          return `${Math.round(n * 10000) / 100}%`;
        };
        const fmtDeployNum = (v: any) =>
          v === null || v === undefined || v === "" ? "—" : String(Number(v));
        const fmtDeployAmount = (v: any) =>
          v === null || v === undefined || v === ""
            ? "—"
            : fmtAmount(Number(v));

        // ---- Equipment cost-head breakdown (Equipment Hourly Use Rate engine
        // heads). The wire carries optional headRates/headAmounts per line,
        // headTotals per norm and grand headTotals on the response — decimals
        // as strings, null heads simply absent. Fixed engine display order;
        // old caches/backends without the fields just render no breakdown.
        const EQUIPMENT_HEAD_ORDER: Array<[string, string]> = [
          ["depreciation", "Depreciation"],
          ["interest", "Interest"],
          ["insurance", "Insurance"],
          ["repair", "Repair"],
          ["misc", "Misc"],
          ["electricity", "Electricity"],
          ["fuel", "Fuel"],
          ["lubricants", "Lubricants"],
          ["crew", "Crew"],
        ];
        const hasHeadValue = (v: any) =>
          v !== null && v !== undefined && v !== "";
        // Rows for one heads object ({depreciation?, …}) → ordered non-null
        // {label, amount} rows (norm/task levels are Amount-only — ₹/hr varies
        // per line, so rates only make sense at line level).
        // `perUnitHeads` is the server's ₹ per unit of output for the SAME
        // grain — absent when that grain mixes output units or has no
        // quantity, in which case the per-unit column shows a dash.
        const headAmountRows = (heads: any, perUnitHeads?: any) =>
          !heads
            ? []
            : EQUIPMENT_HEAD_ORDER.filter(([k]) => hasHeadValue(heads[k])).map(
                ([k, label]) => ({
                  key: k,
                  label,
                  amount: heads[k],
                  perUnit: perUnitHeads?.[k],
                }),
              );
        // Head-first drilldown at TASK level: one head's per-equipment split
        // aggregated across every applied norm's lines (a machine can appear
        // in several matrices — its amounts sum; ₹/hr is the same rate
        // version within one response, so last-write is safe). Lines without
        // that head (missing rate, non-deployed, old cache) don't appear.
        const taskHeadEquipmentRows = (headKey: string) => {
          const byAsset = new Map<number, any>();
          for (const norm of deploymentItems) {
            for (const l of Array.isArray(norm?.lines) ? norm.lines : []) {
              if (!hasHeadValue(l?.headAmounts?.[headKey])) continue;
              const id = Number(l.assetId);
              const acc = byAsset.get(id) ?? {
                key: `${headKey}-${id}`,
                assetCode: l.assetCode,
                assetName: l.assetName,
                rate: l?.headRates?.[headKey],
                perUnitRate: l?.perUnitHeads?.[headKey],
                uomName: l?.uomName,
                hours: 0,
                amount: 0,
                // Every quantity this machine's amounts were priced on. More
                // than one ⇒ the summed amount has no single denominator, so
                // the per-unit figure is suppressed rather than guessed.
                quantities: new Set<string>(),
                units: new Set<string>(),
              };
              // Total working hours of this machine across every norm whose
              // line carries the head (amount = Σ hours × head ₹/hr).
              acc.hours += Number(l.hours) || 0;
              acc.amount += Number(l.headAmounts[headKey]) || 0;
              if (hasHeadValue(l?.headRates?.[headKey])) acc.rate = l.headRates[headKey];
              if (hasHeadValue(l?.perUnitHeads?.[headKey]))
                acc.perUnitRate = l.perUnitHeads[headKey];
              if (hasHeadValue(norm?.appliedQuantity))
                acc.quantities.add(String(norm.appliedQuantity));
              if (l?.uomName) acc.units.add(String(l.uomName));
              byAsset.set(id, acc);
            }
          }
          return Array.from(byAsset.values()).map((acc: any) => {
            const quantities = Array.from(acc.quantities) as string[];
            const units = Array.from(acc.units) as string[];
            // Σ amount ÷ the shared quantity. NOT a sum of per-unit rates —
            // adding ₹/Cum figures priced on different quantities would look
            // like a rate and be an average of averages.
            const denominator = quantities.length === 1 ? Number(quantities[0]) : NaN;
            return {
              ...acc,
              uomName: units.length === 1 ? units[0] : "",
              perUnit:
                units.length === 1 && Number.isFinite(denominator) && denominator > 0
                  ? acc.amount / denominator
                  : undefined,
            };
          });
        };
        const taskHeadRows = headAmountRows(
          deploymentData?.headTotals,
          deploymentData?.perUnitHeadTotals,
        );

        // The output unit behind the per-unit figures. The server only emits
        // perUnitHeadTotals when the applied norms agree on one unit, so this
        // label is either right or absent — it is never a guess.
        const machineryOutputUom =
          deploymentData?.perUnitHeadTotals?.uomName ||
          deploymentItems.find((n: any) => n?.perUnitHeadTotals?.uomName)
            ?.perUnitHeadTotals?.uomName ||
          "";
        const machineryPerUnitTitle = `₹/${machineryOutputUom || "unit"}`;
        // The drilldown's RATE column flips basis; the Amount columns never do
        // — an amount is money, not a rate, and relabelling it would be a lie.
        const showMachineryPerUnit = machineryRateUnit === "OUTPUT";
        const machineryRateUnitSuffix = showMachineryPerUnit
          ? machineryPerUnitTitle
          : "₹/hr";

        const perUnit = (total: number) =>
          workQty > 0
            ? `${fmtAmount(total / workQty)}${workUomLabel ? ` /${workUomLabel}` : ""}`
            : "—";

        const allocated = task.allocatedBudgetAmount ?? task.budgetValue ?? null;
        // Allocated 3M (BOQ + resources + tools) — matches the old Cost Breakup grand total.
        const grandTotal =
          materialBoqTotal + manpowerResTotal + machineryToolsTotal;

        const cards = [
          {
            key: "material" as const,
            label: "Material",
            icon: <AppstoreOutlined />,
            color: "#2557d6",
            tint: "#e5ebfa",
            total: materialTotal,
            lines: materialNormRows.length + boqCount,
          },
          {
            key: "manpower" as const,
            label: "Manpower",
            icon: <TeamOutlined />,
            color: "#d46b08",
            tint: "#fff7e6",
            total: manpowerTotal,
            lines: resourceCount + manpowerItems.length,
          },
          {
            key: "machinery" as const,
            label: "Machinery",
            icon: <ToolOutlined />,
            color: "#531dab",
            tint: "#f9f0ff",
            total: machineryTotal,
            lines: toolCount + machineryNormItems.length + deploymentItems.length,
          },
        ];

        return (
          <>
            {/* 3M summary cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {cards.map((c) => {
                const applied =
                  c.key === "material"
                    ? normGroups
                    : c.key === "manpower"
                    ? manpowerItems
                    : // machinery: head norms + deployment norms both count as
                      // "applied" so the chip cluster renders when only
                      // deployment norms exist.
                      [...machineryNormItems, ...deploymentItems];
                // All three heads support norm apply (machinery since Jul 2026).
                const hasNorm = true;
                return (
                  <Col xs={24} md={8} key={c.key}>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e4e9f0",
                        borderRadius: 12,
                        padding: "14px 16px",
                        height: "100%",
                        boxShadow:
                          "0 1px 3px rgba(2,44,87,0.06), 0 3px 10px rgba(2,44,87,0.05)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: c.tint,
                            color: c.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 17,
                            flexShrink: 0,
                          }}
                        >
                          {c.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              color: "#8094ae",
                            }}
                          >
                            {c.label}
                          </div>
                          <div
                            style={{
                              fontFamily: "Inter, Nunito, sans-serif",
                              fontSize: 18,
                              fontWeight: 700,
                              color: "#022c57",
                              fontVariantNumeric: "tabular-nums",
                              lineHeight: 1.3,
                            }}
                          >
                            {fmtAmount(c.total)}
                          </div>
                          <div style={{ fontSize: 12, color: "#8094ae" }}>
                            {perUnit(c.total)}
                            {c.lines
                              ? ` · ${c.lines} line${c.lines === 1 ? "" : "s"}`
                              : ""}
                          </div>
                        </div>
                      </div>

                      {hasNorm && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            alignItems: "center",
                          }}
                        >
                          {applied.length === 0 ? (
                            <span style={{ color: "#9ca3af", fontSize: 12 }}>
                              No norm applied
                            </span>
                          ) : c.key === "material" ? (
                            normGroups.map((g: any) => (
                              <span key={g.normId} style={chipStyle(c.color, c.tint)}>
                                {g.normCode}
                                {g.normTitle ? ` — ${g.normTitle}` : ""}
                                {canApplyNorm && (
                                  <EditAccess moduleName="Tasks">
                                    <span
                                      role="button"
                                      title="Remove norm"
                                      onClick={() => {
                                        if (removingNormId === g.normId) return;
                                        handleRemoveNorm(
                                          g.normId,
                                          `${g.normCode}${
                                            g.normTitle ? ` — ${g.normTitle}` : ""
                                          }`,
                                        );
                                      }}
                                      style={removeXStyle(removingNormId === g.normId)}
                                    >
                                      ✕
                                    </span>
                                  </EditAccess>
                                )}
                              </span>
                            ))
                          ) : c.key === "manpower" ? (
                            manpowerItems.map((m: any) => (
                              <span
                                key={m.applicationId ?? m.manpowerNormId}
                                style={chipStyle(c.color, c.tint)}
                              >
                                {m.itemCode}
                                {m.itemDescription ? ` — ${m.itemDescription}` : ""}
                                {canApplyNorm && (
                                  <EditAccess moduleName="Tasks">
                                    <span
                                      role="button"
                                      title="Remove item"
                                      onClick={() => {
                                        if (removingManpowerId === m.manpowerNormId)
                                          return;
                                        handleRemoveManpower(
                                          m.manpowerNormId,
                                          `${m.itemCode}${
                                            m.itemDescription
                                              ? ` — ${m.itemDescription}`
                                              : ""
                                          }`,
                                        );
                                      }}
                                      style={removeXStyle(
                                        removingManpowerId === m.manpowerNormId,
                                      )}
                                    >
                                      ✕
                                    </span>
                                  </EditAccess>
                                )}
                              </span>
                            ))
                          ) : (
                            <>
                              {machineryNormItems.map((m: any) => (
                                <span
                                  key={m.applicationId ?? m.machineryNormId}
                                  style={chipStyle(c.color, c.tint)}
                                >
                                  {m.normCode}
                                  {m.description ? ` — ${m.description}` : ""}
                                  {canApplyNorm && (
                                    <EditAccess moduleName="Tasks">
                                      <span
                                        role="button"
                                        title="Remove norm"
                                        onClick={() => {
                                          if (
                                            removingMachineryId ===
                                            m.machineryNormId
                                          )
                                            return;
                                          handleRemoveMachinery(
                                            m.machineryNormId,
                                            `${m.normCode}${
                                              m.description
                                                ? ` — ${m.description}`
                                                : ""
                                            }`,
                                          );
                                        }}
                                        style={removeXStyle(
                                          removingMachineryId ===
                                            m.machineryNormId,
                                        )}
                                      >
                                        ✕
                                      </span>
                                    </EditAccess>
                                  )}
                                </span>
                              ))}
                              {/* Deployment norms ride the same chip cluster,
                                  prefixed ⚙ — a separate entity whose amounts
                                  are live-derived, so they never join the
                                  card's frozen machinery total. */}
                              {deploymentItems.map((n: any) => (
                                <span
                                  key={`dn-${n.applicationId ?? n.deploymentNormId}`}
                                  style={chipStyle(c.color, c.tint)}
                                  title="Equipment norm — hours and amounts derive live from the version in force and equipment rates"
                                >
                                  ⚙ {n.normCode}
                                  {n.description ? ` — ${n.description}` : ""}
                                  {canApplyNorm && (
                                    <EditAccess moduleName="Tasks">
                                      <span
                                        role="button"
                                        title="Remove equipment norm"
                                        onClick={() => {
                                          if (
                                            removingDeploymentId ===
                                            n.deploymentNormId
                                          )
                                            return;
                                          handleRemoveDeployment(
                                            n.deploymentNormId,
                                            `${n.normCode}${
                                              n.description
                                                ? ` — ${n.description}`
                                                : ""
                                            }`,
                                          );
                                        }}
                                        style={removeXStyle(
                                          removingDeploymentId ===
                                            n.deploymentNormId,
                                        )}
                                      >
                                        ✕
                                      </span>
                                    </EditAccess>
                                  )}
                                </span>
                              ))}
                            </>
                          )}
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginTop: "auto",
                          paddingTop: 4,
                        }}
                      >
                        <div style={{ display: "flex", gap: 8 }}>
                          {hasNorm && canApplyNorm && (
                            <EditAccess moduleName="Tasks">
                              <Button
                                size="small"
                                style={{
                                  borderColor: c.color,
                                  color: c.color,
                                  fontWeight: 600,
                                }}
                                onClick={() => {
                                  if (c.key === "material") {
                                    setReapplyNorm(false);
                                    setApplyNormOpen(true);
                                  } else if (c.key === "manpower") {
                                    setReapplyManpower(false);
                                    setApplyManpowerOpen(true);
                                  } else {
                                    setReapplyMachinery(false);
                                    setApplyMachineryOpen(true);
                                  }
                                }}
                              >
                                Apply
                              </Button>
                            </EditAccess>
                          )}
                          {hasNorm &&
                            canApplyNorm &&
                            // Re-apply swaps the modal's own set — deployment
                            // norms have their own Re-apply (drawer Equipment
                            // row), so only HEAD norms gate this button on the
                            // machinery card.
                            (c.key === "machinery"
                              ? machineryNormItems
                              : applied
                            ).length > 0 && (
                            <EditAccess moduleName="Tasks">
                              <Button
                                size="small"
                                style={{
                                  borderColor: c.color,
                                  color: c.color,
                                  fontWeight: 600,
                                }}
                                onClick={() => {
                                  if (c.key === "material") {
                                    setReapplyNorm(true);
                                    setApplyNormOpen(true);
                                  } else if (c.key === "manpower") {
                                    setReapplyManpower(true);
                                    setApplyManpowerOpen(true);
                                  } else {
                                    setReapplyMachinery(true);
                                    setApplyMachineryOpen(true);
                                  }
                                }}
                              >
                                Re-apply
                              </Button>
                            </EditAccess>
                          )}
                        </div>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, fontSize: 13 }}
                          onClick={() => setDrawerKind(c.key)}
                        >
                          View list <RightOutlined style={{ fontSize: 10 }} />
                        </Button>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* 3M grand total vs allocated budget */}
            {(resourceCount > 0 || toolCount > 0 || boqCount > 0) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 20,
                  alignItems: "baseline",
                  marginBottom: 20,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                <span style={{ color: "#526484", fontSize: 13 }}>
                  3M Grand Total{" "}
                  <strong style={{ color: "#022c57" }}>{fmtAmount(grandTotal)}</strong>
                </span>
                {allocated !== undefined && allocated !== null && (
                  <span style={{ color: "#8094ae", fontSize: 12 }}>
                    Allocated Budget {fmtAmount(Number(allocated))}
                  </span>
                )}
              </div>
            )}

            {/* 3M drill-down drawer */}
            <Drawer
              title={
                drawerKind
                  ? `${drawerKind[0].toUpperCase()}${drawerKind.slice(1)} — Details`
                  : ""
              }
              placement="right"
              width={
                drawerExpanded
                  ? Math.min(1200, window.innerWidth - 40)
                  : Math.min(920, window.innerWidth - 60)
              }
              open={!!drawerKind}
              onClose={() => {
                setDrawerKind(null);
                setDrawerExpanded(false);
              }}
              destroyOnClose
              extra={
                <Button
                  type="text"
                  aria-label={drawerExpanded ? "Collapse" : "Expand"}
                  title={drawerExpanded ? "Collapse" : "Expand"}
                  icon={
                    drawerExpanded ? (
                      <FullscreenExitOutlined />
                    ) : (
                      <FullscreenOutlined />
                    )
                  }
                  onClick={() => setDrawerExpanded((v) => !v)}
                />
              }
              footer={
                drawerKind === "material" ? (
                  <div>
                    {materialNormRows.length > 0 && (
                      <div style={drawerFootRow}>
                        <span>Norm Materials Total</span>
                        <span>{fmtAmount(materialNormTotal)}</span>
                      </div>
                    )}
                    {boqCount > 0 && (
                      <div style={drawerFootRow}>
                        <span>BOQ Materials Total</span>
                        <span>{fmtAmount(materialBoqTotal)}</span>
                      </div>
                    )}
                    <div style={{ ...drawerFootRow, ...drawerFootTotal }}>
                      <span>Material Total</span>
                      <span>{fmtAmount(materialTotal)}</span>
                    </div>
                  </div>
                ) : drawerKind === "manpower" ? (
                  <div>
                    <div style={drawerFootRow}>
                      <span>Others Total</span>
                      <span>{fmtAmount(manpowerResTotal)}</span>
                    </div>
                    <div style={drawerFootRow}>
                      <span>PRW Total</span>
                      <span>{fmtAmount(manpowerNormTotal)}</span>
                    </div>
                    <div style={{ ...drawerFootRow, ...drawerFootTotal }}>
                      <span>Manpower Total</span>
                      <span>{fmtAmount(manpowerTotal)}</span>
                    </div>
                  </div>
                ) : drawerKind === "machinery" ? (
                  <div>
                    {machineryToolsTotal > 0 && (
                      <div style={drawerFootRow}>
                        <span>Tools Total</span>
                        <span>{fmtAmount(machineryToolsTotal)}</span>
                      </div>
                    )}
                    {/* Machinery Total includes the live-derived equipment
                        share; its head-wise split lives in the head table. */}
                    <div style={{ ...drawerFootRow, ...drawerFootTotal }}>
                      <span>Machinery Total</span>
                      <span>{fmtAmount(machineryTotal)}</span>
                    </div>
                  </div>
                ) : null
              }
            >
              {drawerKind === "material" && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ color: "#8094ae", fontSize: 12 }}>Applied norm:</span>
                    {normGroups.length === 0 ? (
                      <span style={{ color: "#9ca3af", fontSize: 12 }}>None</span>
                    ) : (
                      normGroups.map((g: any) => (
                        <Tag key={g.normId} color="purple">
                          {g.normCode}
                          {g.normTitle ? ` — ${g.normTitle}` : ""}
                        </Tag>
                      ))
                    )}
                    {canApplyNorm && normGroups.length > 0 && (
                      <EditAccess moduleName="Tasks">
                        <Button
                          size="small"
                          onClick={() => {
                            setDrawerKind(null);
                            setReapplyNorm(true);
                            setApplyNormOpen(true);
                          }}
                        >
                          Re-apply
                        </Button>
                      </EditAccess>
                    )}
                  </div>
                  <div style={sectionLabelStyle}>From Norm</div>
                  <AllocList
                    items={materialNormRows}
                    hasRa={true}
                    raLabel={`Per Unit${workUomLabel ? ` (per ${workUomLabel})` : ""}`}
                    overallLabel={overallLabel}
                    itemHeader="Material"
                    emptyText="No norm materials"
                    getName={(m: any) => (
                      <span style={{ fontWeight: 500, color: "#1a1a2e" }}>
                        {m.materialName || `Material #${m.materialId}`}
                      </span>
                    )}
                    getRa={(m: any) =>
                      m.perUnitQuantity != null
                        ? {
                            qty: m.perUnitQuantity,
                            uom: m.uomName,
                            rate: Number(m.appliedRate) || 0,
                            total:
                              Number(m.perUnitQuantity || 0) *
                              (Number(m.appliedRate) || 0),
                          }
                        : null
                    }
                    getOverall={(m: any) => ({
                      qty: m.appliedQuantity,
                      uom: m.uomName,
                      rate: Number(m.appliedRate) || 0,
                      total:
                        Number(m.appliedQuantity || 0) * (Number(m.appliedRate) || 0),
                    })}
                  />
                </div>
              )}

              {drawerKind === "manpower" && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ color: "#8094ae", fontSize: 12 }}>
                      Applied PRW crew:
                    </span>
                    {manpowerItems.length === 0 ? (
                      <span style={{ color: "#9ca3af", fontSize: 12 }}>None</span>
                    ) : (
                      manpowerItems.map((m: any) => (
                        <Tag key={m.applicationId ?? m.manpowerNormId} color="orange">
                          {m.itemCode}
                          {m.itemDescription ? ` — ${m.itemDescription}` : ""}
                        </Tag>
                      ))
                    )}
                    {canApplyNorm && manpowerItems.length > 0 && (
                      <EditAccess moduleName="Tasks">
                        <Button
                          size="small"
                          onClick={() => {
                            setDrawerKind(null);
                            setReapplyManpower(true);
                            setApplyManpowerOpen(true);
                          }}
                        >
                          Re-apply
                        </Button>
                      </EditAccess>
                    )}
                  </div>
                  <div style={sectionLabelStyle}>Other</div>
                  <AllocList
                    items={task.taskResources || []}
                    hasRa={hasRateAnalysis}
                    raLabel={rateAnalysisLabel}
                    overallLabel={overallLabel}
                    itemHeader="Manpower"
                    emptyText="No 'Others' Norm applied"
                    getName={(r: any) => (
                      <span>
                        <span style={{ fontWeight: 500, color: "#1a1a2e" }}>
                          {r.laborTypeName || "Labour"}
                        </span>
                        {(r.laborCategory || r.laborCategoryName) && (
                          <span style={{ color: "#6b7280" }}>
                            {" "}
                            / {r.laborCategory || r.laborCategoryName}
                          </span>
                        )}{" "}
                        <Tag color="blue" style={{ margin: 0 }}>
                          × {r.resourceCount ?? 0}
                        </Tag>
                      </span>
                    )}
                    getRa={(r: any) =>
                      r.rateAnalysisQty != null
                        ? {
                            qty: r.rateAnalysisQty,
                            uom: r.rateAnalysisUomName,
                            rate: r.rateAnalysisRate,
                            total: raLineTotal(r),
                          }
                        : null
                    }
                    getOverall={(r: any) => ({
                      qty: r.quantity,
                      uom: r.workUomName,
                      rate: r.rate,
                      total: Number(r.totalAmount) || 0,
                    })}
                  />
                  <div style={{ ...sectionLabelStyle, marginTop: 14 }}>
                    PRW
                  </div>
                  <AllocList
                    items={manpowerItems}
                    hasRa={true}
                    raLabel={`Per Unit${workUomLabel ? ` (per ${workUomLabel})` : ""}`}
                    overallLabel={overallLabel}
                    itemHeader="Manpower"
                    emptyText="No manpower applied"
                    getName={(m: any) => (
                      <span>
                        <span style={{ fontWeight: 500, color: "#1a1a2e" }}>
                          {m.itemCode}
                        </span>
                        {m.itemDescription && (
                          <div style={{ color: "#9ca3af", fontSize: 11 }}>
                            {m.itemDescription}
                          </div>
                        )}
                      </span>
                    )}
                    getRa={(m: any) =>
                      m.pucFactor != null && m.pucFactor !== ""
                        ? {
                            qty: m.pucFactor,
                            uom: m.uomName,
                            rate: Number(m.appliedRate) || 0,
                            total:
                              Number(m.pucFactor || 0) *
                              (Number(m.appliedRate) || 0),
                          }
                        : null
                    }
                    getOverall={(m: any) => ({
                      qty: m.appliedQuantity,
                      uom: m.uomName,
                      rate: Number(m.appliedRate) || 0,
                      total:
                        Number(m.appliedQuantity || 0) * (Number(m.appliedRate) || 0),
                    })}
                  />
                </div>
              )}

              {drawerKind === "machinery" && (
                <div>
                  {taskHeadRows.length > 0 ? (
                    <div
                      className="d-flex align-items-center justify-content-end mb-2"
                      style={{ gap: 8 }}
                    >
                      <span style={{ color: "#9ca3af", fontSize: 11 }}>
                        Equipment rate basis
                      </span>
                      <RateUnitToggle uomLabel={machineryOutputUom} />
                    </div>
                  ) : null}
                  {machineryNormLoading && machineryNormItems.length === 0 ? (
                    <span style={{ color: "#999", fontSize: 12 }}>Loading…</span>
                  ) : (
                    // Head tree: every head is a row (₹0 when it has no norms);
                    // expand a head to see its norm lines. Coming-soon heads
                    // show a placeholder and can't be applied yet.
                    <Table
                      size="small"
                      rowKey="key"
                      // The Equipment rows are a DISPLAY-ONLY merge: deployment
                      // norms are a separate entity (not a machinery_norms
                      // head) reached from the same list for discoverability.
                      // Layout per user design: Mucking heads first, then one
                      // Mucking-style row PER COST HEAD (task-level amount,
                      // expand → per-equipment split), Equipment total LAST.
                      // Amounts are live-derived and (per user decision) join machineryTotal.
                      dataSource={[
                        ...machineryNormGroups,
                        ...taskHeadRows.map((row) => ({
                          key: `__HEAD_${row.key}__`,
                          head: row.key,
                          costHead: true,
                          headKey: row.key,
                          headLabel: row.label,
                          items: taskHeadEquipmentRows(row.key),
                          subtotal: row.amount,
                          perUnit: row.perUnit,
                          live: true,
                        })),
                        {
                          key: "__EQUIPMENT__",
                          head: "EQUIPMENT",
                          deployment: true,
                          items: deploymentItems,
                          subtotal: deploymentTotalAmount,
                          live: true,
                        },
                      ]}
                      pagination={false}
                      expandable={{
                        rowExpandable: () => true,
                        expandedRowRender: (grp: any) =>
                          grp.costHead ? (
                            grp.items.length > 0 ? (
                              // Per-equipment split of ONE cost head, summed
                              // across every applied norm's matrix.
                              <Table
                                size="small"
                                rowKey="key"
                                pagination={false}
                                dataSource={grp.items}
                                columns={[
                                  {
                                    title: "Equipment",
                                    key: "asset",
                                    render: (_: any, l: any) => (
                                      <span>
                                        <span style={{ fontWeight: 500, color: "#1a1a2e" }}>
                                          {l.assetName || "—"}
                                        </span>
                                        {l.assetCode ? (
                                          <div style={{ color: "#9ca3af", fontSize: 11 }}>
                                            {l.assetCode}
                                          </div>
                                        ) : null}
                                      </span>
                                    ),
                                  },
                                  {
                                    title: "Hours",
                                    dataIndex: "hours",
                                    key: "hours",
                                    width: 90,
                                    align: "right" as const,
                                    render: (v: any) => (
                                      <span style={{ fontVariantNumeric: "tabular-nums" }}>
                                        {Number(v) ? Number(v) : "—"}
                                      </span>
                                    ),
                                  },
                                  {
                                    // The machine's rate for THIS head, in the
                                    // active basis. ₹/unit comes from the line's
                                    // own prodNorm and factors.
                                    title: machineryRateUnitSuffix,
                                    key: "rate",
                                    width: 120,
                                    align: "right" as const,
                                    render: (_: any, l: any) =>
                                      showMachineryPerUnit
                                        ? l.perUnitRate === undefined ||
                                          l.perUnitRate === null
                                          ? "—"
                                          : moneyPerUnit(l.perUnitRate)
                                        : fmtDeployAmount(l.rate),
                                  },
                                  {
                                    title: "Amount",
                                    dataIndex: "amount",
                                    key: "amount",
                                    width: 130,
                                    align: "right" as const,
                                    render: (v: any) => (
                                      <span style={{ fontVariantNumeric: "tabular-nums" }}>
                                        {fmtAmount(Number(v) || 0)}
                                      </span>
                                    ),
                                  },
                                ]}
                              />
                            ) : (
                              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                                No equipment carries this head
                              </div>
                            )
                          ) : grp.deployment ? (
                            grp.items.length > 0 ? (
                              <div>
                                {grp.items.map((n: any) => (
                                  <div
                                    key={n.applicationId ?? n.deploymentNormId}
                                    style={{
                                      display: "flex",
                                      gap: 12,
                                      fontSize: 12,
                                      padding: "2px 0",
                                    }}
                                  >
                                    <span style={{ fontWeight: 500, color: "#1a1a2e" }}>
                                      {n.normCode}
                                      {n.description ? ` — ${n.description}` : ""}
                                    </span>
                                    <span style={{ color: "#8094ae" }}>
                                      Qty {Number(n.appliedQuantity) || 0} ·{" "}
                                      {Number(n.totalHours) || 0} hrs ·{" "}
                                      {fmtAmount(Number(n.totalAmount) || 0)}
                                    </span>
                                  </div>
                                ))}
                                <div style={{ color: "#9ca3af", fontSize: 11, marginTop: 4 }}>
                                  Full equipment matrices in the Equipment Norms
                                  section below.
                                </div>
                              </div>
                            ) : (
                              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                                No equipment norms applied yet
                              </div>
                            )
                          ) : grp.items.length > 0 ? (
                            <AllocList
                              items={grp.items}
                              hasRa={true}
                              raLabel={`Per Unit${workUomLabel ? ` (per ${workUomLabel})` : ""}`}
                              overallLabel={overallLabel}
                              itemHeader="Machinery"
                              getName={machineryNormGetName}
                              getRa={machineryNormGetRa}
                              getOverall={machineryNormGetOverall}
                            />
                          ) : (
                            <div
                              style={{ color: "#9ca3af", fontSize: 12 }}
                            >
                              {/* This used to read "upload format coming soon"
                                  for the eight placeholder heads. Those are
                                  gone — they were computed columns of the
                                  Equipment Rates master, not heads — so an
                                  empty group now only ever means "nothing
                                  applied to this task yet". */}
                              {`No ${machineryHeadLabel(grp.head)} norms applied yet`}
                            </div>
                          ),
                      }}
                      columns={[
                        {
                          title: "Head",
                          key: "head",
                          render: (_: any, grp: any) =>
                            grp.costHead ? (
                              // A cost head of the Equipment Hourly Use Rate
                              // engine, styled like a machinery head row.
                              <Tag color="cyan" style={{ margin: 0 }}>
                                {grp.headLabel}
                              </Tag>
                            ) : grp.deployment ? (
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <Tag color="geekblue" style={{ margin: 0 }}>
                                  Equipment
                                </Tag>
                                <span style={{ color: "#b0b7c3", fontSize: 11 }}>
                                  {deploymentTotalHours
                                    ? `${deploymentTotalHours} hrs · derived`
                                    : "equipment norms · derived"}
                                </span>
                              </span>
                            ) : (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <Tag
                                color={headTagColor(grp.head)}
                                style={{ margin: 0 }}
                              >
                                {machineryHeadLabel(grp.head)}
                              </Tag>
                              {/* An unregistered head can now only mean the
                                  server returned a category the UI does not
                                  know about — worth flagging, but it is not
                                  "coming soon" any more. */}
                              {!grp.live && (
                                <span
                                  style={{ color: "#b0b7c3", fontSize: 11 }}
                                >
                                  unrecognised head
                                </span>
                              )}
                            </span>
                          ),
                        },
                        {
                          title: "Items",
                          key: "items",
                          width: 80,
                          align: "right" as const,
                          render: (_: any, grp: any) => grp.items.length,
                        },
                        {
                          // ₹ per unit of output for this cost head. Only the
                          // engine cost-head rows have one — Mucking heads and
                          // the Equipment total row are a different grain.
                          title: machineryPerUnitTitle,
                          key: "perUnit",
                          width: 120,
                          align: "right" as const,
                          render: (_: any, grp: any) =>
                            grp.costHead && grp.perUnit !== undefined && grp.perUnit !== null ? (
                              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                                {moneyPerUnit(grp.perUnit)}
                              </span>
                            ) : (
                              <span style={{ color: "#c8cfd8" }}>—</span>
                            ),
                        },
                        {
                          title: "Total",
                          key: "total",
                          width: 130,
                          align: "right" as const,
                          render: (_: any, grp: any) => (
                            <strong style={{ color: "#022c57" }}>
                              {fmtAmount(Number(grp.subtotal) || 0)}
                            </strong>
                          ),
                        },
                        {
                          title: "",
                          key: "action",
                          width: 100,
                          align: "right" as const,
                          // Cost-head rows are read-only derived figures —
                          // applying happens on Mucking heads / the Equipment
                          // row, never on a head.
                          render: (_: any, grp: any) =>
                            canApplyNorm && grp.live && !grp.costHead ? (
                              <EditAccess moduleName="Tasks">
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setDrawerKind(null);
                                    if (grp.deployment) {
                                      // Equipment row routes to the deployment
                                      // norm modal — a separate entity, not a
                                      // machinery head.
                                      setReapplyDeployment(grp.items.length > 0);
                                      setApplyDeploymentOpen(true);
                                      return;
                                    }
                                    setReapplyMachineryHead(grp.head);
                                    setReapplyMachinery(grp.items.length > 0);
                                    setApplyMachineryOpen(true);
                                  }}
                                >
                                  {grp.items.length > 0 ? "Re-apply" : "Apply"}
                                </Button>
                              </EditAccess>
                            ) : null,
                        },
                      ]}
                    />
                  )}

                  {/* Deployment Norms — a PARALLEL block to the head tree
                      above (does not touch machineryNormGroups or
                      machineryTotal). Versioned per-BoQ-code equipment
                      matrices applied to this task: only the quantity is
                      frozen; lines, hours and amounts arrive LIVE-derived
                      from the version-in-force + equipment hourly rates. */}
                  <div style={{ marginTop: 20 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <span style={{ ...sectionLabelStyle, marginBottom: 0 }}>
                        Equipment Norms
                      </span>
                      <span style={{ color: "#8094ae", fontSize: 12 }}>
                        {deploymentItems.length} applied
                        {deploymentItems.length > 0
                          ? ` · ${deploymentTotalHours} hrs · ${fmtAmount(
                              deploymentTotalAmount,
                            )} (derived)`
                          : ""}
                      </span>
                      {deploymentMissingRates > 0 && (
                        <Tag color="orange" style={{ margin: 0 }}>
                          {deploymentMissingRates} rate
                          {deploymentMissingRates === 1 ? "" : "s"} missing
                        </Tag>
                      )}
                      {deploymentAnyNoVersion && (
                        <Tag color="red" style={{ margin: 0 }}>
                          no version
                        </Tag>
                      )}
                      {deploymentNoRateCard && (
                        <Tag color="red" style={{ margin: 0 }}>
                          rate card not set up
                        </Tag>
                      )}
                      <span style={{ flex: 1 }} />
                      {canApplyNorm && (
                        <EditAccess moduleName="Tasks">
                          <Button
                            size="small"
                            onClick={() => {
                              setDrawerKind(null);
                              setReapplyDeployment(false);
                              setApplyDeploymentOpen(true);
                            }}
                          >
                            Apply
                          </Button>
                        </EditAccess>
                      )}
                      {canApplyNorm && deploymentItems.length > 0 && (
                        <EditAccess moduleName="Tasks">
                          <Button
                            size="small"
                            onClick={() => {
                              setDrawerKind(null);
                              setReapplyDeployment(true);
                              setApplyDeploymentOpen(true);
                            }}
                          >
                            Re-apply
                          </Button>
                        </EditAccess>
                      )}
                    </div>

                    {deploymentLoading && deploymentItems.length === 0 ? (
                      <span style={{ color: "#999", fontSize: 12 }}>
                        Loading…
                      </span>
                    ) : deploymentItems.length === 0 ? (
                      <div style={{ color: "#9ca3af", fontSize: 12 }}>
                        No equipment norms applied yet — the quantity freezes
                        at apply; hours and amounts derive live from the
                        version in force and equipment rates.
                      </div>
                    ) : (
                      <Table
                        size="small"
                        rowKey={(r: any) =>
                          r.applicationId ?? r.deploymentNormId
                        }
                        dataSource={deploymentItems}
                        pagination={false}
                        expandable={{
                          rowExpandable: (r: any) =>
                            Array.isArray(r.lines) && r.lines.length > 0,
                          expandedRowRender: (r: any) => (
                            <div>
                            <Table
                              size="small"
                              pagination={false}
                              rowKey={(l: any, i?: number) =>
                                `${r.applicationId}-${l.assetId}-${i}`
                              }
                              dataSource={r.lines || []}
                              columns={[
                                {
                                  title: "Equipment",
                                  key: "asset",
                                  ellipsis: true,
                                  // Name first (what the machine IS), asset
                                  // code beneath in small muted text.
                                  render: (_: any, l: any) => (
                                    <span
                                      style={
                                        l.deployed
                                          ? undefined
                                          : { color: "#b0b7c3" }
                                      }
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {l.assetName || `#${l.assetId}`}
                                        {l.missingRate && (
                                          <Tag
                                            color="orange"
                                            style={{
                                              marginLeft: 6,
                                              fontSize: 11,
                                              lineHeight: "18px",
                                            }}
                                          >
                                            no rate
                                          </Tag>
                                        )}
                                      </span>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: "#b0b7c3",
                                        }}
                                      >
                                        {l.assetCode || `#${l.assetId}`}
                                        {!l.deployed
                                          ? " · considered, not deployed"
                                          : ""}
                                      </div>
                                    </span>
                                  ),
                                },
                                {
                                  title: "Prod. Norm",
                                  key: "prodNorm",
                                  width: 120,
                                  render: (_: any, l: any) => (
                                    <span
                                      style={
                                        l.deployed
                                          ? undefined
                                          : { color: "#b0b7c3" }
                                      }
                                    >
                                      {fmtDeployNum(l.prodNorm)}
                                      {l.deployed && l.uomName ? (
                                        <span
                                          style={{
                                            color: "#8094ae",
                                            fontSize: 11,
                                          }}
                                        >
                                          {" "}
                                          {l.uomName}/Hr
                                        </span>
                                      ) : null}
                                    </span>
                                  ),
                                },
                                {
                                  title: "NPF",
                                  key: "npf",
                                  width: 70,
                                  render: (_: any, l: any) => (
                                    <span
                                      style={
                                        l.deployed
                                          ? undefined
                                          : { color: "#b0b7c3" }
                                      }
                                    >
                                      {fmtDeployPct(l.nonProductionHoursFactor)}
                                    </span>
                                  ),
                                },
                                {
                                  title: "EUF",
                                  key: "euf",
                                  width: 70,
                                  render: (_: any, l: any) => (
                                    <span
                                      style={
                                        l.deployed
                                          ? undefined
                                          : { color: "#b0b7c3" }
                                      }
                                    >
                                      {fmtDeployPct(
                                        l.effectiveUtilizationFactor,
                                      )}
                                    </span>
                                  ),
                                },
                                {
                                  title: "AF",
                                  key: "af",
                                  width: 70,
                                  render: (_: any, l: any) => (
                                    <span
                                      style={
                                        l.deployed
                                          ? undefined
                                          : { color: "#b0b7c3" }
                                      }
                                    >
                                      {fmtDeployPct(l.applicationFactor)}
                                    </span>
                                  ),
                                },
                                {
                                  title: "Hours",
                                  key: "hours",
                                  width: 85,
                                  align: "right" as const,
                                  render: (_: any, l: any) => (
                                    <span
                                      style={
                                        l.deployed
                                          ? { fontWeight: 500 }
                                          : { color: "#b0b7c3" }
                                      }
                                    >
                                      {fmtDeployNum(l.hours)}
                                    </span>
                                  ),
                                },
                                {
                                  title: "Rate",
                                  key: "hourlyRate",
                                  width: 110,
                                  align: "right" as const,
                                  render: (_: any, l: any) => (
                                    <span
                                      style={
                                        l.deployed
                                          ? undefined
                                          : { color: "#b0b7c3" }
                                      }
                                    >
                                      {fmtDeployAmount(l.hourlyRate)}
                                    </span>
                                  ),
                                },
                                {
                                  title: "Amount",
                                  key: "amount",
                                  width: 120,
                                  align: "right" as const,
                                  render: (_: any, l: any) => (
                                    <span
                                      style={
                                        l.deployed
                                          ? { fontWeight: 500 }
                                          : { color: "#b0b7c3" }
                                      }
                                    >
                                      {fmtDeployAmount(l.amount)}
                                    </span>
                                  ),
                                },
                              ]}
                            />
                            </div>
                          ),
                        }}
                        columns={[
                          {
                            title: "Norm",
                            key: "norm",
                            ellipsis: true,
                            render: (_: any, r: any) => (
                              <span>
                                <span
                                  style={{ fontWeight: 500, color: "#1a1a2e" }}
                                >
                                  {r.normCode}
                                </span>
                                {r.status && r.status !== "Active" && (
                                  <Tag
                                    color="red"
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 11,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {r.status}
                                  </Tag>
                                )}
                                {r.noVersion && (
                                  <Tag
                                    color="red"
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 11,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    no version
                                  </Tag>
                                )}
                                {Number(r.missingRateCount) > 0 && (
                                  <Tag
                                    color="orange"
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 11,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {Number(r.missingRateCount)} rate
                                    {Number(r.missingRateCount) === 1
                                      ? ""
                                      : "s"}{" "}
                                    missing
                                  </Tag>
                                )}
                                {r.description && (
                                  <div
                                    style={{ color: "#9ca3af", fontSize: 11 }}
                                  >
                                    {r.description}
                                  </div>
                                )}
                              </span>
                            ),
                          },
                          {
                            title: "Qty",
                            key: "appliedQuantity",
                            width: 80,
                            render: (_: any, r: any) =>
                              fmtDeployNum(r.appliedQuantity),
                          },
                          {
                            title: "Effective From",
                            key: "versionEffectiveFrom",
                            width: 150,
                            render: (_: any, r: any) =>
                              r.versionEffectiveFrom ? (
                                dayjs(r.versionEffectiveFrom).format(
                                  "DD MMM YYYY HH:mm",
                                )
                              ) : (
                                <span style={{ color: "#b0b7c3" }}>—</span>
                              ),
                          },
                          {
                            title: "Hours",
                            key: "totalHours",
                            width: 90,
                            align: "right" as const,
                            render: (_: any, r: any) =>
                              fmtDeployNum(r.totalHours),
                          },
                          {
                            title: "Amount",
                            key: "totalAmount",
                            width: 130,
                            align: "right" as const,
                            render: (_: any, r: any) => (
                              <strong style={{ color: "#022c57" }}>
                                {fmtDeployAmount(r.totalAmount)}
                              </strong>
                            ),
                          },
                          {
                            title: "",
                            key: "action",
                            width: 90,
                            align: "right" as const,
                            render: (_: any, r: any) =>
                              canApplyNorm ? (
                                <EditAccess moduleName="Tasks">
                                  <a
                                    href="#remove"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (
                                        removingDeploymentId ===
                                        r.deploymentNormId
                                      )
                                        return;
                                      handleRemoveDeployment(
                                        Number(r.deploymentNormId),
                                        `${r.normCode}${
                                          r.description
                                            ? ` — ${r.description}`
                                            : ""
                                        }`,
                                      );
                                    }}
                                    style={{
                                      color: "#e85347",
                                      opacity:
                                        removingDeploymentId ===
                                        r.deploymentNormId
                                          ? 0.5
                                          : 1,
                                    }}
                                  >
                                    Remove
                                  </a>
                                </EditAccess>
                              ) : null,
                          },
                        ]}
                      />
                    )}
                  </div>
                </div>
              )}
            </Drawer>
          </>
        );
      })()}

      {/* Monthly production schedule (month-wise phasing of the work quantity) */}
      <TaskScheduleSection task={task} canEdit={canApplyNorm} />

      {/* Monthly actual execution (the executed twin of the schedule) */}
      <TaskActualSection task={task} canEdit={canApplyNorm} />

      {/* Assignees (people — not a 3M) */}
      <SectionCard
        title={
          <span>
            <UserOutlined style={{ marginRight: 6 }} />
            Assignees{" "}
            <span style={{ color: "#8094ae", fontWeight: 400 }}>
              ({userCount})
            </span>
          </span>
        }
        style={{ marginBottom: 12 }}
      >
        {userCount === 0 ? (
          <span style={{ color: "#999", fontSize: 12 }}>None assigned</span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(task.taskUsers || []).map((u: any, i: number) => (
              <Tag
                key={i}
                color="green"
                style={{ borderRadius: 12, padding: "1px 10px" }}
              >
                <UserOutlined style={{ marginRight: 4 }} />
                {u.taskUserName || u.userName || "Unknown"}
              </Tag>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Tool split-materials drawer */}
      <Drawer
        title={
          materialsDrawerTool
            ? `Split Materials — ${materialsDrawerTool.toolName || "Tool"}`
            : "Split Materials"
        }
        placement="right"
        width={Math.min(
          760,
          typeof window !== "undefined" ? window.innerWidth - 60 : 760,
        )}
        open={!!materialsDrawerTool}
        onClose={() => setMaterialsDrawerTool(null)}
        destroyOnClose
      >
        {materialsDrawerTool && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 12,
                fontSize: 13,
                color: "#6b7280",
              }}
            >
              <span>
                {materialsDrawerTool.toolName}
                {materialsDrawerTool.serialNumber
                  ? ` · ${materialsDrawerTool.serialNumber}`
                  : ""}
              </span>
              <span>
                Tool Total:{" "}
                <strong style={{ color: "#004293" }}>
                  {fmtAmount(Number(materialsDrawerTool.totalAmount) || 0)}
                </strong>
              </span>
            </div>
            <ToolMaterialsView
              materials={materialsDrawerTool.toolMaterials || []}
              materialMap={materialMap}
              subMaterialMap={subMaterialMap}
              showRa={hasRateAnalysis}
              raLabel={rateAnalysisLabel}
              overallLabel={overallLabel}
              showHeader={false}
            />
            {(() => {
              const mats = materialsDrawerTool.toolMaterials || [];
              const raTotal = mats.reduce(
                (s: number, m: any) => s + raLineTotal(m),
                0,
              );
              const overallTotal = mats.reduce(
                (s: number, m: any) => s + (Number(m.totalAmount) || 0),
                0,
              );
              return (
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 8,
                    borderTop: "1px dashed #d9d9d9",
                    fontSize: 13,
                  }}
                >
                  {hasRateAnalysis && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "2px 0",
                        color: "#004293",
                      }}
                    >
                      <span>Rate Analysis Total (per unit)</span>
                      <strong>{fmtAmount(raTotal)}</strong>
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "2px 0",
                      color: "#237804",
                      fontWeight: 600,
                    }}
                  >
                    <span>Overall Materials Total</span>
                    <span>{fmtAmount(overallTotal)}</span>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </Drawer>

      {/* Checklist modal for status change */}
      <TaskCheckListCreateModal
        open={showChecklistModal}
        checklistId={selectedChecklistForModal?.checklistId ?? null}
        checklistLinkId={selectedChecklistForModal?.checklistLinkId ?? null}
        onClose={() => setShowChecklistModal(false)}
        onSave={(success: boolean | undefined) =>
          handleChecklistModalClose("save", success)
        }
      />

      {/* OnHold modal: capture reason + expected resume date */}
      <Modal
        title="Put Task On Hold"
        open={onHoldModalVisible}
        onOk={handleOnHoldSubmit}
        onCancel={() => {
          setOnHoldModalVisible(false);
          setPendingOnHoldStatus(null);
          setOnHoldReason("");
          setOnHoldETA(null);
        }}
        okText="Submit"
        cancelText="Cancel"
        okButtonProps={{ type: "primary" }}
        confirmLoading={statusUpdating}
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
            >
              <span style={{ color: "#666" }}>
                Reason for putting task on hold
              </span>{" "}
              <span style={{ color: "red" }}>*</span>
            </label>
            <Input.TextArea
              placeholder="Please provide the reason for putting this task on hold..."
              rows={3}
              style={{ width: "100%" }}
              value={onHoldReason}
              onChange={(e) => setOnHoldReason(e.target.value)}
            />
          </div>
          <div>
            <label
              style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
            >
              <span style={{ color: "#666" }}>Expected date to resume</span>{" "}
              <span style={{ color: "red" }}>*</span>
            </label>
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Select expected resume date"
              format="YYYY-MM-DD"
              value={onHoldETA}
              onChange={(date) => setOnHoldETA(date)}
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
            />
          </div>
        </div>
      </Modal>

      {/* Executed Rate modal for task closure */}
      <Modal
        title="Rate Confirmation"
        open={executedRateModalVisible}
        onOk={handleExecutedRateSubmit}
        onCancel={() => {
          setExecutedRateModalVisible(false);
          setPendingClosureStatus(null);
          setExecutedRate(null);
          setRateRemarks("");
          onRefresh?.();
        }}
        okText="Submit"
        cancelText="Cancel"
        okButtonProps={{ type: "primary" }}
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
            >
              <span style={{ color: "#666" }}>Planned Task Rate:</span>{" "}
              <span style={{ color: "#2557d6", fontWeight: 600 }}>
                {task?.rate ?? 0}
                {task?.workUomShortCode || task?.workUomName
                  ? ` per ${task?.workUomShortCode || task?.workUomName}`
                  : ""}
              </span>
            </label>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
            >
              <span style={{ color: "#666" }}>Executed Rate</span>{" "}
              <span style={{ color: "red" }}>*</span>
            </label>
            <Input
              style={{ width: "100%" }}
              placeholder="Enter the actual rate at which task was executed"
              type="number"
              min={0}
              step={0.01}
              value={executedRate !== null ? executedRate.toString() : ""}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                setExecutedRate(value);
                if (value !== null && Number(value) === (task?.rate || 0)) {
                  setRateRemarks("");
                }
              }}
            />
          </div>
          {executedRate !== null &&
            Number(executedRate) !== (task?.rate || 0) && (
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontWeight: 500,
                  }}
                >
                  <span style={{ color: "#666" }}>Remarks</span>{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <Input.TextArea
                  placeholder="Please explain why the executed rate differs from the planned task rate"
                  rows={3}
                  style={{ width: "100%" }}
                  value={rateRemarks}
                  onChange={(e) => setRateRemarks(e.target.value)}
                />
              </div>
            )}
        </div>
      </Modal>

      <ApplyNormModal
        open={applyNormOpen}
        onClose={() => {
          setApplyNormOpen(false);
          setReapplyNorm(false);
        }}
        task={{
          taskId: Number(task?.taskId) || 0,
          taskName: task?.taskName,
          workType: task?.workType,
          workQuantity: task?.workQuantity,
          outputQuantity: task?.outputQuantity,
        }}
        replaceMode={reapplyNorm}
        replaceNormId={reapplyNorm ? normGroups?.[0]?.normId : undefined}
        onApplied={() => {
          fetchNormMaterials();
          onRefresh?.();
        }}
      />

      <ApplyManpowerNormModal
        open={applyManpowerOpen}
        onClose={() => {
          setApplyManpowerOpen(false);
          setReapplyManpower(false);
        }}
        task={{
          taskId: Number(task?.taskId) || 0,
          taskName: task?.taskName,
          workType: task?.workType,
          workQuantity: task?.workQuantity,
          outputQuantity: task?.outputQuantity,
        }}
        replaceMode={reapplyManpower}
        replaceManpowerNormIds={
          reapplyManpower
            ? manpowerItems.map((m: any) => m.manpowerNormId)
            : undefined
        }
        onApplied={() => {
          fetchManpowerItems();
          onRefresh?.();
        }}
      />

      <ApplyMachineryNormModal
        open={applyMachineryOpen}
        onClose={() => {
          setApplyMachineryOpen(false);
          setReapplyMachinery(false);
          setReapplyMachineryHead(null);
        }}
        task={{
          taskId: Number(task?.taskId) || 0,
          taskName: task?.taskName,
          workType: task?.workType,
          workQuantity: task?.workQuantity,
          outputQuantity: task?.outputQuantity,
        }}
        initialHead={reapplyMachineryHead ?? undefined}
        replaceMode={reapplyMachinery}
        replaceMachineryNormIds={
          // Head-scoped re-apply: preselect (and later swap) only this head's
          // currently-applied norms.
          reapplyMachinery
            ? machineryNormItems
                .filter(
                  (m: any) =>
                    (m.category || "") === (reapplyMachineryHead ?? ""),
                )
                .map((m: any) => m.machineryNormId)
            : undefined
        }
        onApplied={() => {
          fetchMachineryNormItems();
          onRefresh?.();
        }}
      />

      <ApplyDeploymentNormModal
        open={applyDeploymentOpen}
        onClose={() => {
          setApplyDeploymentOpen(false);
          setReapplyDeployment(false);
        }}
        task={{
          taskId: Number(task?.taskId) || 0,
          taskName: task?.taskName,
          workType: task?.workType,
          workQuantity: task?.workQuantity,
        }}
        replaceMode={reapplyDeployment}
        replaceDeploymentNormIds={
          reapplyDeployment
            ? (deploymentData?.items || []).map((i: any) =>
                Number(i.deploymentNormId),
              )
            : undefined
        }
        onApplied={() => {
          fetchDeploymentNorms();
          onRefresh?.();
        }}
      />
    </div>
  );
};

export default TaskQuickViewCard;
